<template>
  <v-container fluid>
    <v-snackbar color="success" v-model="snackbar" multi-line>
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn color="red" variant="text" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <v-layout row wrap>
      <v-flex xs12>
        <v-autocomplete
          :items="customerList"
          v-model="activeCustomer"
          label="Select a Customer"
          item-text="name"
          item-value="key"
          clearable
          prepend-icon="refresh"
          @click:prepend="refreshData"
        ></v-autocomplete>
      </v-flex>
    </v-layout>
    <v-layout v-if="activeCustomer === null" row wrap>
      <v-flex class="text-xs-center" xs12>OR</v-flex>
      <v-flex xs12>
        <v-btn color="primary" dark block to="/Admin/Customer">
          <v-icon>add</v-icon> Add New Customer
        </v-btn>
      </v-flex>
    </v-layout>

    <v-layout wrap>
      <v-flex
        v-for="(card, i) in projectSummaryCards"
        :key="i"
        v-if="projectStates[i] > 0"
      >
        <v-card class="pa-2 ma-1 text-xs-center">
          <v-progress-circular
            v-if="i < 5 || includeArchive === true"
            :size="70"
            :width="10"
            :rotate="360"
            :value="(projectStates[i] / totalProjects) * 100"
            :color="card.color"
          >
            {{ projectStates[i] }}
          </v-progress-circular>
          <v-btn
            v-else
            round
            class="my-3"
            :color="card.color"
            dark
            @click="getArchive"
            >Load Complete Projects</v-btn
          >
          <div class="title">{{ card.title }}</div>
        </v-card>
      </v-flex>
    </v-layout>

    <v-card class="mt-2 mb-3">
      <v-card-title>
        <!-- Project dialog -->
        <v-dialog v-model="projectDialog" max-width="90%" persistent>
          <v-btn
            color="primary"
            dark
            slot="activator"
            class="mb-2"
            @click="getNewProjectId"
            v-show="activeCustomer"
            >New Project</v-btn
          >
          <v-card>
            <v-card-title class="mb-0 pb-0">
              <v-flex xs6>
                <span class="headline">{{ projectFormTitle }}</span>
              </v-flex>
              <v-flex xs6>
                <v-combobox
                  v-model="bookingEmailAddress"
                  label="Send booking email(s)"
                  chips
                  deletable-chips
                  multiple
                  browser-autocomplete="new-email"
                  :success-messages="emailSuccess"
                  :error-messages="emailError"
                >
                  <template v-slot:append-outer>
                    <v-fade-transition leave-absolute>
                      <v-progress-circular
                        v-if="sendingEmail"
                        size="24"
                        color="primary"
                        indeterminate
                      ></v-progress-circular>
                      <v-btn
                        @click="sendBookingEmail"
                        :disabled="bookingEmailAddress.length < 1"
                        color="primary"
                        flat
                        v-else
                        icon
                        ><v-icon>send</v-icon></v-btn
                      >
                    </v-fade-transition>
                  </template>
                </v-combobox>
              </v-flex>
            </v-card-title>
            <v-card-text>
              <v-container grid-list-md>
                <v-layout wrap>
                  <v-flex
                    xs12
                    v-if="gettingNewProjectId"
                    class="text-xs-center"
                  >
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="50"
                    ></v-progress-circular>
                  </v-flex>
                  <v-flex xs12 v-else>
                    <v-alert
                      type="error"
                      v-model="saveProjectFailed"
                      dismissible
                      >An error occured when trying to save the project. Please
                      try again.</v-alert
                    >
                    <v-form ref="newProjectForm" v-model="newProjectValid">
                      <v-subheader>General</v-subheader>
                      <v-layout wrap>
                        <v-flex xs12 sm6 md4>
                          <v-text-field
                            box
                            v-model="editedProject.projectId"
                            readonly
                            label="Project ID"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <!-- <v-menu ref="projectDateDialog" v-model="projectDateDialog" lazy transition="scale-transition" offset-y full-width :nudge-right="40"
                            min-width="290px" :return-value.sync="editedProject.Date" :close-on-content-click="false"> -->
                          <v-text-field
                            box
                            label="Created Date"
                            :value="editedProject.CreatedDate | formatDate"
                            readonly
                          ></v-text-field>
                          <!-- <v-date-picker v-model="editedProject.CreatedDate" scrollable @input="$refs.projectDateDialog.save(editedProject.Date)"> -->
                          <!-- </v-date-picker> -->
                          <!-- </v-menu> -->
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-select
                            box
                            v-model="editedProject.priority"
                            label="Priority"
                            item-text="text"
                            item-value="value"
                            :items="[
                              { text: 'High', value: 1 },
                              { text: 'Medium', value: 2 },
                              { text: 'Low', value: 3 },
                            ]"
                          ></v-select>
                        </v-flex>
                        <v-flex xs12 sm6 md4 v-if="userData.Role === 'Admin'">
                          <v-text-field
                            box
                            v-model="editedProject.priorityOrder"
                            label="Priority Number"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            box
                            v-model="editedProject.jobType"
                            label="Job Type"
                            :items="['Order', 'Service Call']"
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            box
                            v-model="editedProject.Status"
                            label="Project Status"
                            :items="[
                              'CTN Sheet Approved',
                              'In Progress',
                              'In Progress HW setup',
                              'In Progress Shipped',
                              'Install In Progress',
                              'Complete',
                              'Cancelled',
                              'In Query',
                            ]"
                            clearable
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            box
                            v-model="editedProject.manager"
                            multiple
                            required
                            label="Project Manager(s)"
                            :rules="[(v) => !!v || 'Item is required']"
                            item-value="Name"
                            item-text="Name"
                            :items="projectManagers"
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            box
                            v-model="editedProject.marketingStickers"
                            label="Allowed Marketing Stickers"
                            :items="['Yes', 'No']"
                          ></v-autocomplete>
                        </v-flex>
                      </v-layout>
                      <v-subheader>Supplier</v-subheader>
                      <v-layout wrap>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            box
                            v-model="editedProject.Supplier"
                            label="Assign to Supplier"
                            :items="suppliers"
                            item-text="name"
                            item-value="key"
                            clearable
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-text-field
                            box
                            v-model="editedProject.supplierCost"
                            type="number"
                            label="Supplier Cost"
                            prefix="£"
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                      <v-subheader>Zendesk</v-subheader>
                      <v-layout wrap>
                        <v-flex xs12 sm6 md4>
                          <v-text-field
                            box
                            v-model="editedProject.customerTicket"
                            label="Zendesk - Customer Ticket"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-text-field
                            box
                            v-model="editedProject.BDMTicket"
                            label="Zendesk - BDM Order Ticket"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-text-field
                            box
                            v-model="editedProject.engineerTicket"
                            label="Zendesk - Engineer Ticket"
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                      <v-subheader>Finance - Customer</v-subheader>
                      <v-layout wrap>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            box
                            v-model="editedProject.financeCustomer"
                            label="Finance Status - Customer"
                            :items="[
                              'Not Started',
                              'Invoiced',
                              'No Costs to Invoice',
                              'Query',
                            ]"
                            clearable
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-text-field
                            box
                            v-model="editedProject.financeInvoiced"
                            label="Finance - Customer Invoiced"
                            type="number"
                            step="0.01"
                            prefix="£"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-text-field
                            box
                            v-model="editedProject.customerPO"
                            label="Customer PO Number"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-text-field
                            box
                            v-if="editedProject.jobType === 'Service Call'"
                            v-model="editedProject.customerCost"
                            label="Customer Cost"
                            type="number"
                            step="0.01"
                            prefix="£"
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                      <v-subheader>Finance - Supplier</v-subheader>
                      <v-layout wrap>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            box
                            v-model="editedProject.financeSupplier"
                            label="Finance Status - Supplier"
                            :items="[
                              'Not Started',
                              'In Query',
                              'Supplier part Paid',
                              'Complete - Supplier Paid',
                              'Complete - No Cost',
                            ]"
                            clearable
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-text-field
                            box
                            v-model="editedProject.supplierPaid"
                            label="Supplier Amount Paid"
                            type="number"
                            step="0.01"
                            prefix="£"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-text-field
                            box
                            v-model="editedProject.supplierInvoiceNumbers"
                            label="Supplier Invoice Numbers"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12>
                          <v-textarea
                            outline
                            label="Notes"
                            v-model="editedProject.notes"
                          ></v-textarea>
                        </v-flex>
                      </v-layout>
                      <upload-btn
                        uniqueId
                        :title="'Attach New Document'"
                        :multiple="true"
                        :fileChangedCallback="attachFile"
                      >
                        <template slot="icon">
                          <v-icon color="white">mdi-paperclip</v-icon>
                        </template>
                      </upload-btn>
                      <v-progress-linear
                        :color="fileProgress === 100 ? 'success' : 'primary'"
                        :value="fileProgress"
                      ></v-progress-linear>
                      <v-list class="mt-3" avatar>
                        <v-list-tile
                          v-for="(doc, i) in editedProject.attachedDocs"
                          :key="i"
                        >
                          <v-list-tile-avatar
                            ><v-icon
                              >mdi-file-document-outline</v-icon
                            ></v-list-tile-avatar
                          >
                          <v-list-tile-content>
                            <v-list-tile-title>
                              <a :href="doc.downloadUrl" target="_BLANK">{{
                                doc.name
                              }}</a>
                            </v-list-tile-title>
                          </v-list-tile-content>
                          <v-list-tile-action
                            v-if="
                              userData.UID === 'KmPhBoqfuxcqM2Vu6ES1JHx0Yzd2' ||
                              userData.UID === 'XPq26H0FBcYICFHg6JbUvfVC2fn2' ||
                              userData.UID === 'b8yNX9oGK8UcntezqIupoXt8BBl2' ||
                              userData.UID === 'HxvYXLfcVVc2LuVW55Wv9b9EZOk1'
                            "
                            ><v-btn icon @click="deleteFile(doc)"
                              ><v-icon>delete</v-icon></v-btn
                            ></v-list-tile-action
                          >
                        </v-list-tile>
                      </v-list>
                    </v-form>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-btn
                color="red darken-1"
                flat
                @click.native="deleteProjectModal(editedProject)"
                >Delete Project</v-btn
              >
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" flat @click.native="closeProject"
                >Close</v-btn
              >
              <v-btn
                color="green darken-1"
                flat
                @click.native="saveProject(true)"
                :disabled="!newProjectValid"
                :loading="savingSaveProject"
                >Save</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-select
          label="Filter by Status"
          :items="projectSummaryCards"
          item-value="title"
          item-text="title"
          v-model="projectStatusFilter"
          multiple
          clearable
          single-line
          hide-details
          chips
        ></v-select>
        <v-spacer></v-spacer>
        <v-select
          label="Filter by Type"
          :items="[
            { value: '0', title: 'All Types' },
            { value: 'sc', title: 'Service Calls' },
            { value: 'p', title: 'Project' },
          ]"
          item-value="value"
          item-text="title"
          v-model="projectTypeFilter"
          single-line
          hide-details
        ></v-select>
        <v-spacer></v-spacer>
        <v-text-field
          append-icon="search"
          label="Project Manager Filter"
          single-line
          hide-details
          v-model="activeManager"
        ></v-text-field>
        <v-spacer></v-spacer>
        <v-text-field
          append-icon="search"
          label="Search"
          single-line
          hide-details
          v-model="searchProject"
        ></v-text-field>
      </v-card-title>
      <v-data-table
        :rows-per-page-items="[
          25,
          50,
          100,
          { text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 },
        ]"
        :headers="projectHeaders"
        :items="filteredProjects"
        :search="searchProject"
        item-key="id"
        :loading="projectTableLoading"
        :pagination.sync="projectsPagination"
      >
        <template slot="items" slot-scope="props">
          <tr :class="props.item.priority === 1 ? 'red lighten-4' : ''">
            <td
              @click.stop="
                if (activeCustomer === props.item.Customer) {
                  props.expanded = !props.expanded;
                  customerToRetrieve = props.item.Customer;
                } else {
                  openProject(props.item);
                }
              "
            >
              <a v-show="activeCustomer === null"
                >{{ props.item.projectId }}
                <v-icon small>mdi-open-in-new</v-icon></a
              ><span v-show="activeCustomer !== null">{{
                props.item.projectId
              }}</span>
            </td>
            <td>{{ props.item.qnq }}</td>
            <td v-if="activeCustomer === null">
              <a @click="activeCustomer = props.item.Customer">{{
                customerNameFromID(props.item.Customer)
              }}</a>
            </td>
            <td
              @click.stop="
                props.expanded = !props.expanded;
                customerToRetrieve = props.item.Customer;
              "
            >
              <v-chip
                v-if="props.item.CreatedDate == today"
                color="green"
                text-color="white"
                small
                >New</v-chip
              >{{ props.item.CreatedDate | formatDate }}
            </td>
            <td
              v-if="activeCustomer === null"
              @click.stop="
                props.expanded = !props.expanded;
                customerToRetrieve = props.item.Customer;
              "
            >
              {{ props.item.BDM }}
            </td>
            <td
              v-if="activeCustomer !== null"
              @click.stop="
                props.expanded = !props.expanded;
                customerToRetrieve = props.item.Customer;
              "
            >
              {{ supplierNameFromID(props.item.Supplier) }}
            </td>
            <td
              @click.stop="
                props.expanded = !props.expanded;
                customerToRetrieve = props.item.Customer;
              "
            >
              {{ props.item.Status ? props.item.Status : "Awaiting Approval" }}
            </td>
            <td
              @click.stop="
                props.expanded = !props.expanded;
                customerToRetrieve = props.item.Customer;
              "
            >
              {{ props.item.manager }}
            </td>
            <td
              @click.stop="
                props.expanded = !props.expanded;
                customerToRetrieve = props.item.Customer;
              "
            >
              {{ props.item.priorityText }}
            </td>
            <td
              @click.stop="
                props.expanded = !props.expanded;
                customerToRetrieve = props.item.Customer;
              "
            >
              {{ props.item.priorityOrder }}
            </td>
            <td class="text-xs-right">
              <v-btn icon class="mx-0" @click.stop="editProject(props.item)">
                <v-icon color="teal">edit</v-icon>
              </v-btn>
              <v-btn
                icon
                class="mx-0"
                @click.stop="deleteProjectModal(props.item)"
              >
                <v-icon color="pink">delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
        <template slot="expand" slot-scope="props">
          <v-layout justify-center>
            <div
              v-for="(summary, i) in projectSummary(props.item.projectId)"
              :key="i"
            >
              <v-chip dark>
                <v-avatar class="primary">{{ summary }}</v-avatar>
                {{
                  templates.find((t) => {
                    return t.id === i;
                  }).name
                }}
              </v-chip>
            </div>
          </v-layout>
          <v-layout justify-center>
            <v-flex xs8>
              <v-card class="ma-2 elevation-0" width="100%">
                <v-list dark dense>
                  <v-list-tile>
                    <v-layout>
                      <v-flex xs3>
                        <v-list-tile-content> Status </v-list-tile-content>
                      </v-flex>
                      <v-flex xs3>
                        <v-list-tile-content> Vehicle </v-list-tile-content>
                      </v-flex>
                      <v-flex xs3>
                        <v-list-tile-content> Date </v-list-tile-content>
                      </v-flex>
                      <v-flex xs3>
                        <v-list-tile-content> Job Number </v-list-tile-content>
                      </v-flex>
                    </v-layout>
                  </v-list-tile>
                  <hr />
                  <template
                    v-for="(job, i) in expandedJobs(props.item.projectId)"
                  >
                    <v-list-tile :key="i" @click="expandProjectJob(job)">
                      <v-layout>
                        <v-flex xs3>
                          <v-list-tile-avatar>
                            <v-icon
                              :color="
                                job.Status >= 2
                                  ? 'green'
                                  : job.Status === 1
                                  ? 'red'
                                  : job.Supplier === '' || job.Supplier === null
                                  ? 'grey'
                                  : job.Allocated === '' ||
                                    job.Allocated === null
                                  ? 'cyan'
                                  : 'orange'
                              "
                              >assignment</v-icon
                            >
                          </v-list-tile-avatar>
                        </v-flex>
                        <v-flex xs3>
                          <v-list-tile-content>
                            {{
                              (job.Vehicle
                                ? vehicleRegFromID(job.Vehicle)
                                : job.vehReg) | uppercase
                            }}
                          </v-list-tile-content>
                        </v-flex>
                        <v-flex xs3>
                          <v-list-tile-content>
                            {{ job.Date | formatDate }}
                          </v-list-tile-content>
                        </v-flex>
                        <v-flex xs3>
                          <v-list-tile-content>
                            {{ job.JobNo }}
                          </v-list-tile-content>
                        </v-flex>
                      </v-layout>
                    </v-list-tile>
                    <v-list-tile
                      v-if="job === expandedProjectJob"
                      v-for="product in job.templates"
                      :key="product"
                    >
                      <v-layout>
                        <v-flex xs12>
                          <v-list-tile-content>
                            {{
                              templates.find((t) => {
                                return t.id === product;
                              }).name
                            }}
                          </v-list-tile-content>
                        </v-flex>
                      </v-layout>
                    </v-list-tile>
                  </template>
                </v-list>
              </v-card>
            </v-flex>
          </v-layout>
          <v-layout
            row
            wrap
            v-if="props.item.attachedDocs && props.item.attachedDocs.length > 0"
          >
            <v-flex xs12>
              <v-card class="my-2 pa-2 elevation-0" width="100%">
                <v-list class="mt-3" avatar>
                  <h5 class="title">Attached Documents</h5>
                  <v-list-tile
                    v-for="doc in props.item.attachedDocs"
                    :key="doc.name"
                  >
                    <v-list-tile-avatar
                      ><v-icon
                        >mdi-file-document-outline</v-icon
                      ></v-list-tile-avatar
                    >
                    <v-list-tile-content>
                      <v-list-tile-title>
                        <a :href="doc.downloadUrl" target="_BLANK">{{
                          doc.name
                        }}</a>
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>
              </v-card>
            </v-flex>
          </v-layout>
          <v-layout
            row
            wrap
            v-if="props.item.contacts && props.item.contacts.length > 0"
          >
            <v-flex xs12>
              <v-card class="my-2 pa-2 elevation-0" width="100%">
                <h5 class="title">Contacts</h5>
                <v-data-table
                  :items="contacts"
                  hide-actions
                  :headers="[
                    { text: 'Name', value: 'Name' },
                    { text: 'Email', value: 'EmailAddress' },
                    { text: 'Phone', value: 'Phone' },
                    { text: 'Mobile', value: 'Mobile' },
                    { text: 'Type', value: 'Type', sortable: false },
                  ]"
                >
                  <template slot="items" slot-scope="props1">
                    <tr
                      v-if="props.item.contacts.indexOf(props1.item.key) !== -1"
                    >
                      <td>{{ props1.item.Name }}</td>
                      <td>
                        <a :href="'mailto:' + props1.item.EmailAddress">{{
                          props1.item.EmailAddress
                        }}</a>
                      </td>
                      <td>{{ props1.item.Phone }}</td>
                      <td>{{ props1.item.Mobile }}</td>
                      <td>
                        <v-tooltip bottom v-if="props1.item.Fleet">
                          <v-chip slot="activator" small
                            ><v-icon>mdi-truck</v-icon></v-chip
                          >
                          <span>Fleet</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="props1.item.Comms">
                          <v-chip slot="activator" small
                            ><v-icon>mdi-phone</v-icon></v-chip
                          >
                          <span>Comms</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="props1.item.Accounts">
                          <v-chip slot="activator" small
                            ><v-icon>mdi-cash-100</v-icon></v-chip
                          >
                          <span>Accounts</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card>
            </v-flex>
          </v-layout>
          <v-layout row wrap v-if="props.item.notes">
            <v-flex xs12>
              <v-card class="my-2 pa-2 elevation-0" width="100%">
                <h5 class="title"><v-icon>speaker_notes</v-icon> Notes</h5>
                <v-textarea
                  readonly
                  solo
                  flat
                  v-model="props.item.notes"
                ></v-textarea>
              </v-card>
            </v-flex>
          </v-layout>
        </template>
      </v-data-table>
    </v-card>

    <v-layout v-if="activeCustomer !== null" wrap>
      <v-flex v-for="(card, i) in summaryCards" :key="i">
        <v-card class="pa-2 ma-1 text-xs-center">
          <v-progress-circular
            :size="70"
            :width="10"
            :rotate="360"
            :value="(states[i] / totalJobs) * 100"
            :color="card.color"
          >
            {{ states[i] }}
          </v-progress-circular>
          <div class="title">{{ card.title }}</div>
        </v-card>
      </v-flex>
    </v-layout>

    <v-card v-if="activeCustomer !== null" class="mt-2">
      <v-card-title>
        <!-- Job dialog -->
        <v-btn color="secondary" @click="copyJobsData"
          ><v-icon>mdi-content-copy</v-icon> Copy Data</v-btn
        >
        <bulk-import-export-dialog
          :parentCustomer="activeCustomer"
          @jobsUpdated="getJobs"
          @updatingVehicleReg="updatedVehicleReg"
        ></bulk-import-export-dialog>
        <merge-jobs-dialog
          :openDialog="mergeJobsDialog"
          :jobs="selected"
          :mergeJobsDialog.sync="mergeJobsDialog"
        ></merge-jobs-dialog>
        <v-dialog v-model="dialog" max-width="90%" scrollable>
          <v-btn color="primary" dark slot="activator" class="mb-2"
            >New Job</v-btn
          >
          <v-card>
            <v-card-title>
              <span class="headline">{{ formTitle }}</span>
            </v-card-title>
            <v-card-text>
              <v-container grid-list-md>
                <v-layout wrap>
                  <v-flex xs12>
                    <v-alert type="info" :value="editedItem.Status > 1"
                      >This job has already been completed, please only edit the
                      job if details were incorrect at time of install.</v-alert
                    >
                    <v-alert type="error" v-model="saveJobFailed" dismissible
                      >An error occured when trying to save the job. Please try
                      again.</v-alert
                    >
                    <v-form ref="newJob" v-model="newJobValid">
                      <v-layout wrap>
                        <v-flex xs12>
                          <v-textarea
                            label="notes"
                            outline
                            v-model="editedItem.notes"
                          ></v-textarea>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            label="Vehicle"
                            :items="vehicles"
                            :loading="gettingVehicles"
                            item-value="VehicleReg"
                            item-text="VehicleReg"
                            v-model="vehicleSelect"
                            :rules="[(v) => !!v || 'Item is required']"
                            required
                            clearable
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            required
                            label="Select Customer"
                            :items="customerList"
                            v-model="editedItem.Customer"
                            item-text="name"
                            item-value="key"
                            dense
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            label="Select Site"
                            readonly
                            :items="sites"
                            v-model="editedItem.VehicleSite"
                            item-text="Name"
                            item-value="key"
                            hint="What site is this vehicle at?"
                            dense
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            :rules="[(v) => !!v || 'Item is required']"
                            required
                            label="Select Products"
                            :items="products"
                            v-model="selectProducts"
                            multiple
                            chips
                            item-text="name"
                            item-value="id"
                            hint="What products does this job need?"
                            persistent-hint
                            deletable-chips
                            dense
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            label="Assign to Supplier"
                            :items="suppliers"
                            v-model="supplierSelect"
                            item-text="name"
                            item-value="key"
                            clearable
                            disabled
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            label="Assign to Engineer"
                            :items="supplierSelectEngineers"
                            v-model="editedItem.Allocated"
                            item-text="name"
                            item-value="key"
                            :loading="gettingEngineers"
                            clearable
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-autocomplete
                            :rules="[(v) => !!v || 'Item is required']"
                            required
                            label="Assign to Project"
                            :items="filteredCustomerProjectsByEditedCustomer"
                            v-model="editedItem.Project"
                            item-text="projectId"
                            item-value="projectId"
                            clearable
                            @change="setJobSupplier"
                          ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-menu
                            ref="dateDialog"
                            v-model="dateDialog"
                            lazy
                            transition="scale-transition"
                            offset-y
                            full-width
                            :nudge-right="40"
                            min-width="290px"
                            :return-value.sync="editedItem.Date"
                            :close-on-content-click="false"
                          >
                            <v-text-field
                              slot="activator"
                              label="Install Date"
                              :value="editedItem.Date | formatDate"
                              prepend-icon="event"
                              readonly
                            ></v-text-field>
                            <v-date-picker
                              v-model="editedItem.Date"
                              scrollable
                              @input="$refs.dateDialog.save(editedItem.Date)"
                            >
                            </v-date-picker>
                          </v-menu>
                        </v-flex>
                        <template v-if="oldExtras">
                          <template v-for="(item, index) in editedItem.extras">
                            <v-flex
                              xs12
                              sm6
                              md4
                              v-if="item.length"
                              :key="index"
                            >
                              <v-text-field
                                type="text"
                                :label="index"
                                v-model="editedItem.extras[index]"
                              ></v-text-field>
                            </v-flex>
                          </template>
                        </template>
                        <v-flex v-else xs12 sm6 md4>
                          <v-btn @click="oldExtras = true"
                            >Show Old Extras</v-btn
                          >
                        </v-flex>
                        <v-flex xs12>
                          <v-layout
                            wrap
                            v-for="(
                              field, index
                            ) in editedItem.additionalFields"
                            :key="index"
                          >
                            <v-flex xs4 md2>
                              <v-text-field
                                label="Field Name"
                                v-model="field['fieldName']"
                              >
                              </v-text-field>
                            </v-flex>
                            <v-flex xs7 md4>
                              <v-text-field
                                label="Field Value"
                                v-model="field['fieldValue']"
                              >
                              </v-text-field>
                            </v-flex>
                            <v-flex xs1>
                              <v-btn
                                flat
                                icon
                                color="primary"
                                @click="
                                  editedItem.additionalFields.splice(index, 1)
                                "
                              >
                                <v-icon>close</v-icon>
                              </v-btn>
                            </v-flex>
                          </v-layout>
                        </v-flex>
                        <v-btn
                          color="primary"
                          @click="addAdditionalField"
                          ref="additionalFieldBtn"
                        >
                          <v-icon left>add</v-icon>Additional Field</v-btn
                        >
                        <v-flex xs12>
                          <template
                            v-for="product in editedItem.templateExtras"
                          >
                            <h4 :key="product.id">
                              Product: {{ product.product.name }}
                            </h4>
                            <v-layout :key="product.id" wrap row>
                              <template
                                v-for="(item, index) in productExtras.filter(
                                  (p) => p.id === product.product.id
                                )"
                              >
                                <v-flex
                                  v-if="editedItem.extras[product.product.id]"
                                  xs12
                                  sm6
                                  md4
                                  :key="index"
                                >
                                  <v-select
                                    v-if="item.type === 'choice'"
                                    :label="item.name"
                                    :items="item.options"
                                    v-model="
                                      editedItem.extras[product.product.id][
                                        item.name
                                      ]
                                    "
                                    :required="item.required === true"
                                    :clearable="
                                      item.default === '' ? true : false
                                    "
                                  ></v-select>
                                  <v-text-field
                                    type="text"
                                    v-else-if="item.type === 'text'"
                                    :label="item.name"
                                    v-model="
                                      editedItem.extras[product.product.id][
                                        item.name
                                      ]
                                    "
                                    :required="item.required === true"
                                  ></v-text-field>
                                  <v-text-field
                                    type="number"
                                    :step="item.step"
                                    v-else
                                    :label="item.name"
                                    v-model="
                                      editedItem.extras[product.product.id][
                                        item.name
                                      ]
                                    "
                                    :required="item.required === true"
                                  ></v-text-field>
                                </v-flex>
                              </template>
                            </v-layout>
                            <template
                              class="px-4"
                              v-for="device in product.templateDetails"
                            >
                              <template v-if="device.extras !== undefined">
                                <v-card :key="device.id">
                                  <h5>Device: {{ device.name }}</h5>
                                  <table
                                    style="width: 100%"
                                    v-if="device.deviceDetails"
                                  >
                                    <thead>
                                      <th
                                        :key="detail.id"
                                        v-for="detail in device.extras"
                                      >
                                        {{ detail.name }}
                                      </th>
                                    </thead>
                                    <tbody>
                                      <td
                                        :key="detail.id"
                                        class="pa-2 text-sm-center"
                                        v-for="detail in device.extras"
                                      >
                                        <span>{{
                                          device.deviceDetails[detail.name]
                                        }}</span>
                                      </td>
                                    </tbody>
                                  </table>
                                  <template v-for="detail in device.extras">
                                    <v-autocomplete
                                      class="mx-4 mt-2"
                                      box
                                      label="Select Device"
                                      append-outer-icon="refresh"
                                      :key="detail.name"
                                      v-if="
                                        detail.type === 'uid' &&
                                        !device.deviceDetails
                                      "
                                      @change="
                                        (v) => {
                                          device.deviceDetails = v;
                                        }
                                      "
                                      @click:append-outer="forceUpdate"
                                      item-value="id"
                                      return-object
                                      item-text="id"
                                      :items="
                                        devices.filter(
                                          (d) =>
                                            d.type === device.id && !d.vehicle
                                        )
                                      "
                                      :value="
                                        device.deviceDetails !== undefined
                                          ? device.deviceDetails[detail.name]
                                          : ''
                                      "
                                    />
                                  </template>
                                </v-card>
                              </template>
                            </template>
                          </template>
                        </v-flex>
                      </v-layout>
                    </v-form>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" flat @click.native="close"
                >Cancel</v-btn
              >
              <v-btn
                color="green darken-1"
                flat
                @click.native="save"
                :disabled="!newJobValid"
                :loading="savingSaveJob"
                >Save</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-spacer></v-spacer>
        <v-select
          label="Filter by Status"
          :items="Statuses"
          item-value="value"
          item-text="text"
          v-model="statusFilter"
          multiple
          clearable
          single-line
          hide-details
        ></v-select>
        <v-spacer></v-spacer>
        <v-text-field
          append-icon="search"
          label="Search"
          single-line
          hide-details
          v-model="search"
        ></v-text-field>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="filteredJobs"
        :search="search"
        v-model="selected"
        ref="jobsTable"
        item-key="id"
        select-all
        :loading="tableLoading"
        :pagination.sync="pagination"
      >
        <template slot="items" slot-scope="props">
          <tr>
            <td>
              <v-checkbox
                primary
                hide-details
                v-model="props.selected"
              ></v-checkbox>
            </td>
            <td>
              <v-edit-dialog
                :return-value.sync="updateRegV"
                lazy
                large
                persistent
                @save="updateReg(props.item)"
              >
                <div>
                  <v-btn small icon><v-icon>mdi-pencil</v-icon></v-btn>
                  {{ props.item.Vehicle }}
                </div>
                <template v-slot:input>
                  <v-text-field
                    :value="props.item.Vehicle"
                    @change="(v) => (updateRegV = v)"
                  />
                </template>
              </v-edit-dialog>
            </td>
            <td @click.stop="props.expanded = !props.expanded">
              {{ props.item.VehicleMake }}
            </td>
            <td @click.stop="props.expanded = !props.expanded">
              {{ props.item.VehicleModel }}
            </td>
            <td @click.stop="props.expanded = !props.expanded">
              {{ props.item.VehicleYear }}
            </td>
            <td @click.stop="props.expanded = !props.expanded">
              {{ props.item.CompletedDate | formatDate }}
            </td>
            <td @click.stop="props.expanded = !props.expanded">
              {{ props.item.Engineer }}
            </td>
            <td @click.stop="props.expanded = !props.expanded">
              {{ props.item.currency || "£" }}{{ props.item.cost || 0 }}
            </td>
            <td @click.stop="props.expanded = !props.expanded">
              {{ props.item.Project }}
            </td>
            <td @click.stop="props.expanded = !props.expanded">
              {{ props.item.JobNo }}
            </td>
            <td @click.stop="props.expanded = !props.expanded">
              {{ props.item.Status | jobStatus(props.item.Allocated) }}
            </td>
            <td class="text-xs-right">
              <v-tooltip
                v-if="props.item.jobStarted && props.item.Status == 0"
                bottom
              >
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" icon class="mx-0">
                    <v-icon color="blue">account_box</v-icon>
                  </v-btn>
                </template>
                <span
                  >Engineer has started this job but not sent it back to portal
                  yet</span
                >
              </v-tooltip>
              <v-btn
                v-if="props.item.Status > 0"
                icon
                class="mx-0"
                @click.stop="viewJob(props.item.id)"
              >
                <v-icon v-if="props.item.Status > 2" color="primary"
                  >assignment_turned_in</v-icon
                >
                <v-icon v-else-if="props.item.Status == 2" color="success"
                  >assignment_ind</v-icon
                >
                <v-icon v-else color="red">assignment_late</v-icon>
              </v-btn>
              <v-btn v-if="props.item.chargeable" icon class="mx-0">
                <v-icon color="black">mdi-currency-gbp</v-icon>
              </v-btn>
              <v-btn
                v-if="props.item.Status < 3"
                icon
                class="mx-0"
                @click.stop="approveJob(props.item.id)"
              >
                <v-icon color="green">check</v-icon>
              </v-btn>
              <v-btn icon class="mx-0" @click.stop="editItem(props.item.id)">
                <v-icon color="teal">edit</v-icon>
              </v-btn>
              <v-btn icon class="mx-0" @click.stop="deleteItem(props.item)">
                <v-icon color="pink">delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
        <template slot="expand" slot-scope="props">
          <v-layout justify-center>
            <v-card class="ma-2">
              {{ mergePropsNotes(props.item.notes, props.item.Notes) }}
              <v-list dark dense>
                <v-list-tile
                  v-for="(template, i) in props.item.templates"
                  v-bind:key="i"
                >
                  <v-list-tile-action>
                    <v-checkbox disabled input-value="true"></v-checkbox>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title>{{
                      templates.find((t) => {
                        return t.id === template;
                      }).name
                    }}</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
            </v-card>
          </v-layout>
        </template>
      </v-data-table>
    </v-card>
    <completed-view
      :visible.sync="viewDialog"
      :JobID="currentJob"
      @approveJob="approveJob"
      @issueJob="issueJob"
    ></completed-view>

    <!-- Delete project dialog -->
    <v-dialog v-model="deleteProjectDialog" max-width="750px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">Delete Project</span>
        </v-card-title>
        <v-card-text
          >Please confirm whether you would also like to delete the related jobs
          with the project. Alternatively, you may want to cancel the project
          instead of deleting it.</v-card-text
        >
        <v-card-actions>
          <v-btn
            color="red darken-1"
            flat
            @click.native="deleteProject(false)"
            :loading="deletingProjectOnly"
            :disabled="deletingProjectAll || cancelProject"
            >Delete project only</v-btn
          >
          <v-btn
            color="red darken-1"
            flat
            @click.native="deleteProject(true)"
            :loading="deletingProjectAll"
            :disabled="deletingProjectOnly || cancelProject"
            >Delete project and jobs</v-btn
          >
          <!-- <v-btn color="orange darken-1" flat @click.native="cancelProject" :loading="cancelProject" :disabled="deletingProjectAll || deletingProjectOnly">Cancel Project</v-btn> -->
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            right
            flat
            @click.native="deleteProjectDialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Change products dialog -->
    <v-dialog v-model="changeProductsDialog" max-width="750px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">Change Products</span>
        </v-card-title>
        <v-card-text>
          <v-alert :value="!allProductsMatch" color="error"
            >All of the selected jobs don't currently share the same
            products.</v-alert
          >
          <v-autocomplete
            :rules="[(v) => !!v || 'Item is required']"
            required
            label="Select Products"
            :items="products"
            v-model="allSelectedProducts"
            multiple
            chips
            item-text="name"
            item-value="id"
            hint="What products does this job need?"
            persistent-hint
            deletable-chips
            dense
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="blue darken-1"
            flat
            @click.native="changeProductsDialog = false"
            >Close</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn
            color="green darken-1"
            flat
            @click.native="saveChangedProducts"
            :loading="savingSaveJob"
            :disabled="!allSelectedProducts.length"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add to project dialog -->
    <v-dialog v-model="addToProjectDialog" max-width="500px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline mr-4">Assign Jobs to Project</span>
          <a v-if="!createNewProject" @click="createNewProject = true"
            >create new project?</a
          >
          <a v-else @click="createNewProject = false">cancel new project?</a>
        </v-card-title>
        <v-card-text>
          <v-form>
            <v-autocomplete
              v-if="createNewProject === false"
              label="Assign to Project"
              :items="filteredCustomerProjects"
              v-model="assignToProject"
              item-text="projectId"
              item-value="projectId"
            ></v-autocomplete>
            <template v-if="createNewProject === true">
              <v-autocomplete
                label="Project to copy"
                :items="projects"
                v-model="duplicateProjectSource"
                item-text="projectId"
                item-value="projectId"
              ></v-autocomplete>
              <v-autocomplete
                label="Supplier"
                :items="suppliers"
                v-model="duplicateProjectSupplier"
                item-text="name"
                item-value="key"
              ></v-autocomplete>
              <v-btn
                :disabled="duplicateProjectSource === null"
                @click="createDuplicateProject"
                >Create New Project</v-btn
              >
            </template>
            <h3>Jobs to be assigned</h3>
            <v-list dense v-if="selected">
              <v-list-tile v-for="(job, index) in selected" :key="index">
                {{ job.JobNo }}
              </v-list-tile>
            </v-list>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="green darken-1"
            flat
            @click.native="addJobsToProject"
            :disabled="assignToProject === null"
            :loading="savingAddJobsToProject"
            >Save</v-btn
          >
          <v-btn
            color="blue darken-1"
            right
            flat
            @click.native="addToProjectDialog = false"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Assign to engineer dialog -->
    <v-dialog v-model="assignToEngineerDialog" max-width="500px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">Assign Jobs to Engineer</span>
        </v-card-title>
        <v-card-text>
          <v-form>
            <v-autocomplete
              label="Assign to Supplier"
              :items="suppliers"
              v-model="assignToSupplier"
              item-text="name"
              item-value="key"
              disabled
            ></v-autocomplete>
            <v-autocomplete
              label="Assign to Engineer"
              :items="assignToSupplierEngineers"
              v-model="assignToEngineer"
              item-text="name"
              item-value="key"
              :loading="gettingEngineers"
              clearable
              :disabled="!assignToSupplier"
            ></v-autocomplete>
            <h3>Jobs to be assigned</h3>
            <v-list dense v-if="selected">
              <v-list-tile v-for="(job, index) in selected" :key="index">
                {{ job.JobNo }}
              </v-list-tile>
            </v-list>
            <h5 v-if="!assignToSupplier" class="error--text">
              A Supplier must be assigned to the selected jobs parent project
            </h5>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="green darken-1"
            flat
            @click.native="assignJobsToEngineer"
            :loading="savingAssignJobsToEngineer"
            :disabled="!assignToSupplier"
            >Save</v-btn
          >
          <v-btn
            color="blue darken-1"
            right
            flat
            @click.native="assignToEngineerDialog = false"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete jobs dialog -->
    <v-dialog v-model="deleteJobsDialog" max-width="500px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">Delete Jobs</span>
        </v-card-title>
        <v-card-text>
          <h3>You are about to delete these jobs</h3>
          <v-list dense v-if="selected">
            <v-list-tile v-for="(job, index) in selected" :key="index">
              {{ job.JobNo }}
            </v-list-tile>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="red darken-1"
            flat
            @click.native="deleteMultiJobs"
            :loading="deletingJobs"
            >Delete Jobs</v-btn
          >
          <v-btn
            color="blue darken-1"
            right
            flat
            @click.native="deleteJobsDialog = false"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Approve jobs dialog -->
    <v-dialog v-model="approveJobsDialog" max-width="500px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">Approve Jobs</span>
        </v-card-title>
        <v-card-text>
          <h3>You are about to approve these jobs</h3>
          <v-list dense v-if="selected">
            <v-list-tile v-for="(job, index) in selected" :key="index">
              {{ job.JobNo }}
            </v-list-tile>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="green"
            flat
            @click.native="approveMultiJobs"
            :loading="approvingJobs"
            >Approve Jobs</v-btn
          >
          <v-btn
            color="blue darken-1"
            right
            flat
            @click.native="approveJobsDialog = false"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Approve job dialog -->
    <v-dialog v-model="approveDialog" max-width="500px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">Approve Job</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="approvalReg"
            label="Update Vehicle Reg"
          ></v-text-field>
          <h4>You are about to approve job {{ currentJobNo }}.</h4>
          <template
            v-if="
              jobs.find((job) => {
                return job.id === this.currentJob;
              })
            "
          >
            <h5>Job Attachments</h5>
            <v-list-tile
              v-for="(doc, i) in jobs.find((job) => {
                return job.id === this.currentJob;
              }).completedDocs"
              :key="i"
            >
              <v-list-tile-avatar
                ><v-icon>mdi-file-document-outline</v-icon></v-list-tile-avatar
              >
              <v-list-tile-content>
                <v-list-tile-title>
                  <a :href="doc.downloadUrl" target="_BLANK">{{ doc.name }}</a>
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </template>
          <upload-btn
            uniqueId
            :title="'Attach New Document'"
            :multiple="true"
            :fileChangedCallback="attachCompletedFile"
            ref="btnAttachCompletedFile"
          >
            <template slot="icon">
              <v-icon color="white">mdi-paperclip</v-icon>
            </template>
          </upload-btn>
          <v-progress-linear
            :color="fileProgress === 100 ? 'success' : 'primary'"
            :value="fileProgress"
          ></v-progress-linear>
        </v-card-text>
        <v-card-actions
          v-if="
            jobs.find((job) => {
              return job.id === this.currentJob;
            })
          "
        >
          <v-spacer></v-spacer>
          <v-btn
            color="green"
            flat
            @click.native="approveCurrentJob"
            :loading="approvingJob"
            :disabled="
              jobs.find((job) => {
                return job.id === this.currentJob;
              }).Status < 1 &&
              !jobs.find((job) => {
                return job.id === this.currentJob;
              }).completedDocs
            "
          >
            Approve Job
          </v-btn>
          <v-btn
            color="blue darken-1"
            right
            flat
            @click.native="approveDialog = false"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Approve job dialog -->
    <v-dialog v-model="issueDialog" max-width="500px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">Flag Issue With Job</span>
        </v-card-title>
        <v-card-text>
          <h4>You are about to flag an issue with job {{ currentJobNo }}.</h4>
          <template
            v-if="
              jobs.find((job) => {
                return job.id === this.currentJob;
              })
            "
          >
            <v-textarea v-model="JobNotes" outline label="Notes"></v-textarea>
          </template>
        </v-card-text>
        <v-card-actions
          v-if="
            jobs.find((job) => {
              return job.id === this.currentJob;
            })
          "
        >
          <v-spacer></v-spacer>
          <v-btn
            color="red"
            flat
            @click.native="issueCurrentJob"
            :loading="issueingJob"
            >Issue With Job</v-btn
          >
          <v-btn
            color="blue darken-1"
            right
            flat
            @click.native="issueDialog = false"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Assign Install Date dialog -->
    <v-dialog v-model="assignInstallDateDialog" max-width="500px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">Assign Install Date</span>
        </v-card-title>
        <v-card-text>
          <v-menu
            ref="assignDateDialog"
            v-model="assignDateDialog"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            :nudge-right="40"
            min-width="290px"
            return-value.sync="assignInstallDate"
            :close-on-content-click="false"
          >
            <v-text-field
              slot="activator"
              label="Install Date"
              :value="assignInstallDate | formatDate"
              prepend-icon="event"
              readonly
            ></v-text-field>
            <v-date-picker
              v-model="assignInstallDate"
              scrollable
              @input="$refs.assignDateDialog.save(assignInstallDate)"
            >
            </v-date-picker>
          </v-menu>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="green"
            flat
            @click.native="assignInstallDates"
            :loading="assigningDates"
            >Save</v-btn
          >
          <v-btn
            color="blue darken-1"
            right
            flat
            @click.native="assignInstallDateDialog = false"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- update cost dialog -->
    <v-dialog
      v-model="bulkUpdateCostDialog"
      max-width="500px"
      scrollable
      persistent
    >
      <v-card>
        <v-card-title> Update Cost </v-card-title>
        <v-card-text>
          <v-select
            label="Currency"
            :items="['£', '€']"
            v-model="bulkUpdateCurrency"
          />
          <v-text-field type="number" v-model="bulkUpdateCost" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success darken-1"
            :loading="updatingCosts"
            flat
            @click.native="updateCosts"
            >Update</v-btn
          >
          <v-btn
            color="blue darken-1"
            right
            flat
            @click.native="bulkUpdateCostDialog = false"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-fab-transition>
      <v-speed-dial
        v-model="fab"
        v-show="selected.length > 0"
        bottom
        right
        fixed
        color="primary"
      >
        <v-btn slot="activator" color="primary" dark fab hover v-model="fab">
          <v-icon>edit</v-icon>
          <v-icon>close</v-icon>
        </v-btn>
        <v-tooltip
          :disabled="tooltipsDisabled"
          left
          :value="tooltips"
          color="pink"
        >
          <v-btn
            slot="activator"
            fab
            dark
            small
            color="pink"
            @click="deleteJobsDialog = true"
          >
            <v-icon>delete</v-icon>
          </v-btn>
          <span>Delete</span>
        </v-tooltip>
        <v-tooltip
          :disabled="tooltipsDisabled"
          left
          :value="tooltips"
          color="secondary"
        >
          <v-btn
            slot="activator"
            fab
            dark
            small
            color="secondary"
            @click="changeProducts"
          >
            <v-icon>mdi-swap-horizontal</v-icon>
          </v-btn>
          <span>Change Products</span>
        </v-tooltip>
        <v-tooltip
          v-if="selected.length === 2"
          :disabled="tooltipsDisabled"
          left
          :value="tooltips"
          color="light-blue"
        >
          <v-btn
            slot="activator"
            fab
            dark
            small
            color="light-blue"
            @click="mergeJobsDialog = true"
          >
            <v-icon>mdi-set-center</v-icon>
          </v-btn>
          <span>Merge</span>
        </v-tooltip>
        <v-tooltip
          v-if="selectedSameProject"
          color="indigo"
          :disabled="tooltipsDisabled"
          left
          :value="tooltips"
        >
          <v-btn
            slot="activator"
            fab
            dark
            small
            color="indigo"
            @click="assignToEngineerDialog = true"
          >
            <v-icon>assignment_ind</v-icon>
          </v-btn>
          <span>Assign Engineer</span>
        </v-tooltip>
        <v-tooltip
          :disabled="tooltipsDisabled"
          left
          :value="tooltips"
          color="red"
        >
          <v-btn
            slot="activator"
            fab
            dark
            small
            color="red"
            @click="assignInstallDateDialog = true"
          >
            <v-icon>mdi-calendar</v-icon>
          </v-btn>
          <span>Assign Install Date</span>
        </v-tooltip>
        <v-tooltip
          :disabled="tooltipsDisabled"
          left
          :value="tooltips"
          color="orange"
        >
          <v-btn
            slot="activator"
            fab
            dark
            small
            color="orange"
            @click="addToProjectDialog = true"
          >
            <v-icon>library_add</v-icon>
          </v-btn>
          <span>Change Project</span>
        </v-tooltip>
        <v-tooltip
          :disabled="tooltipsDisabled"
          left
          :value="tooltips"
          color="green"
        >
          <v-btn
            slot="activator"
            fab
            dark
            small
            color="green"
            @click="approveJobsDialog = true"
          >
            <v-icon>check</v-icon>
          </v-btn>
          <span>Approve Jobs</span>
        </v-tooltip>
        <v-tooltip
          :disabled="tooltipsDisabled"
          left
          :value="tooltips"
          color="purple"
        >
          <v-btn
            slot="activator"
            fab
            dark
            small
            color="purple"
            @click="bulkUpdateCostDialog = true"
          >
            £
          </v-btn>
          <span>Add Cost</span>
        </v-tooltip>
        <v-tooltip
          :disabled="tooltipsDisabled"
          left
          :value="tooltips"
          color="yellow"
        >
          <v-btn
            slot="activator"
            fab
            dark
            small
            color="yellow"
            @click="printSelectedJobs"
          >
            <v-icon>print</v-icon>
          </v-btn>
          <span>Print</span>
        </v-tooltip>
      </v-speed-dial>
    </v-fab-transition>
  </v-container>
</template>

<script>
import { sendEmail, updateAuditLog, fireStore, storage } from "../firebase";
import moment from "moment";
import UploadButton from "vuetify-upload-button";
export default {
  components: {
    "upload-btn": UploadButton,
  },
  beforeRouteLeave(to, from, next) {
    if (this.activeCustomer !== null) {
      const answer = window.confirm(
        "Do you really want to leave projects? If not click cancel and click the cross to the right of the selected customer to return to the projects homepage."
      );
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  props: ["customer", "job", "project"],
  data() {
    return {
      expandedProjectJob: null,
      selected: [],
      allProductsMatch: true,
      allSelectedProducts: [],
      selectedProject: "P-A&M0003",
      selectedOpenProject: [],
      saveJobFailed: false,
      saveProjectFailed: false,
      activeManager: "",
      fab: false,
      changeProductsDialog: false,
      summaryCards: [
        { title: "Not Started", color: "grey" },
        { title: "Awaiting Install", color: "orange" },
        { title: "Issue", color: "red" },
        { title: "Complete", color: "cyan" },
        { title: "Complete - Cubo Approved", color: "green" },
      ],
      projectSummaryCards: [
        { title: "Awaiting Approval", color: "grey" },
        { title: "In Query", color: "yellow" },
        { title: "CTN Sheet Approved", color: "cyan" },
        { title: "Cancelled", color: "red" },
        { title: "In Progress", color: "orange" },
        { title: "In Progress HW setup", color: "orange" },
        { title: "In Progress Shipped", color: "orange" },
        { title: "Install In Progress", color: "orange" },
        { title: "Complete", color: "green" },
      ],
      projectTypeFilter: "0",
      bookingEmailAddress: [],
      userData: this.$store.getters.userData,
      sendingEmail: false,
      openProjectStatuses: [
        "Awaiting Approval",
        "CTN Sheet Approved",
        "In Progress",
        "In Progress HW setup",
        "In Progress Shipped",
        "Install In Progress",
        "In Query",
      ],
      addToProjectDialog: false,
      expanded: false,
      newJobValid: false,
      activeCustomer: null,
      viewDialog: false,
      updatingCosts: false,
      bulkUpdateCost: 0,
      bulkUpdateCurrency: "£",
      updateCost: null,
      updateCurrency: null,
      updateAllocated: null,
      bulkUpdateCostDialog: false,
      managerList: [],
      mergeJobsDialog: false,
      includeArchive: true,
      currentJob: "",
      gettingEngineers: false,
      gettingVehicles: false,
      dateDialog: false,
      projectDateDialog: false,
      JobNotes: "",
      oldExtras: false,
      sites: [],
      jobs: [],
      pagination: {
        sortBy: "Project",
        rowsPerPage: 25,
        page: 1,
        descending: true,
      },
      projectsPagination: {
        sortBy: "CreatedDate",
        rowsPerPage: 25,
        page: 1,
        descending: true,
      },
      openProjectsPagination: {
        sortBy: "projectId",
        rowsPerPage: 25,
        page: 1,
        descending: true,
      },
      headers: [
        {
          text: "Vehicle",
          value: "Vehicle",
          align: "left",
        },
        {
          text: "Make",
          value: "VehicleMake",
          align: "left",
        },
        {
          text: "Model",
          value: "VehicleModel",
          align: "left",
        },
        {
          text: "Year",
          value: "VehicleYear",
          align: "left",
        },
        {
          text: "Completed Date",
          value: "CompletedDate",
          align: "left",
        },
        {
          text: "Engineer",
          value: "Engineer",
          align: "left",
        },
        {
          text: "Engineer Cost",
          value: "cost",
          align: "left",
        },
        {
          text: "Project",
          value: "Project",
          align: "left",
        },
        {
          text: "Job Number",
          value: "JobNo",
          align: "left",
        },
        {
          text: "Status",
          value: "Status",
          align: "left",
        },
        {
          text: "Actions",
          value: "actions",
          align: "right",
          sortable: false,
        },
      ],
      openProjectHeaders: [
        {
          text: "Project ID",
          value: "projectId",
          align: "left",
        },
        {
          text: "QNQ Number",
          value: "qnq",
          align: "left",
        },
        {
          text: "Customer",
          value: "Customer",
          align: "left",
        },
        {
          text: "Created Date",
          value: "CreatedDate",
          align: "left",
        },
        {
          text: "Supplier",
          value: "Supplier",
          align: "left",
        },
        {
          text: "Project Status",
          value: "Status",
          align: "left",
        },
        {
          text: "Project Manager(s)",
          value: "manager",
          align: "left",
        },
      ],
      search: "",
      searchProject: "",
      searchOpenProject: "",
      dialog: false,
      editedIndex: -1,
      engineers: [],
      vehicles: [],
      selectProducts: [],
      editedItem: {
        Vehicle: null,
        vehReg: "",
        JobNo: null,
        templates: ["preCheck"],
        productExtras: [],
        products: [],
        Supplier: "",
        Allocated: "",
        Date: null,
        Status: 0,
        templateExtras: [],
        additionalFields: [],
        extras: {},
        Project: null,
        notes: "",
      },
      supplierSelect: "",
      vehicleSelect: "",
      defaultItem: {
        Vehicle: null,
        vehReg: "",
        JobNo: null,
        templates: ["preCheck"],
        productExtras: [],
        products: [],
        Supplier: "",
        Allocated: "",
        Date: null,
        Status: 0,
        templateExtras: [],
        additionalFields: [],
        extras: {},
        Project: null,
        notes: "",
      },
      tableLoading: false,
      projectTableLoading: false,
      openProjectTableLoading: false,
      states: [],
      projectStates: [],
      totalJobs: 0,
      totalProjects: 0,
      productExtras: [],
      editedProjectIndex: -1,
      projectDialog: false,
      newProjectValid: false,
      projectOrdering: [],
      editedProject: {
        id: "",
        totalJobs: 0,
        completedJobs: 0,
        Status: 0,
        manager: "",
        CreatedDate: null,
        BDM: "",
        customerTicket: "",
        engineerTicket: "",
        BDMTicket: "",
        financeCustomer: 0,
        financeInvoiced: null,
        financeSupplier: 0,
        supplierPaid: null,
        customerPO: null,
        customerCost: null,
        jobType: null,
        priorityOrder: null,
      },
      defaultProject: {
        id: "",
        totalJobs: 0,
        completedJobs: 0,
        Status: 0,
        manager: "",
        CreatedDate: null,
        BDM: "",
        customerTicket: "",
        engineerTicket: "",
        BDMTicket: "",
        financeCustomer: 0,
        financeInvoiced: null,
        financeSupplier: 0,
        supplierPaid: null,
        customerPO: null,
        customerCost: null,
        jobType: null,
        priorityOrder: null,
      },
      projects: [],
      emailSuccess: "",
      emailError: "",
      fileProgress: 0,
      gettingNewProjectId: false,
      newProject: false,
      deleteProjectDialog: false,
      deleteProjectData: {},
      assignToProject: null,
      assignToEngineerDialog: false,
      assignToEngineer: "",
      assignToSupplier: "",
      contacts: [],
      savingAddJobsToProject: false,
      savingAssignJobsToEngineer: false,
      savingSaveProject: false,
      savingSaveJob: false,
      cancelProject: false,
      deletingProjectAll: false,
      deletingProjectOnly: false,
      deleteJobsDialog: false,
      deletingJobs: false,
      approveJobsDialog: false,
      approvalReg: "",
      assignInstallDateDialog: false,
      assignDateDialog: false,
      assigningDates: false,
      assignInstallDate: null,
      approvingJobs: false,
      approvingJob: false,
      approveDialog: false,
      issueingJob: false,
      issueDialog: false,
      projectManagers: [],
      statusFilter: [],
      projectStatusFilter: [
        "In Progress",
        "In Progress HW setup",
        "In Progress Shipped",
        "Install In Progress",
        "Cancelled",
        "CTN Sheet Approved",
        "In Query",
        "Awaiting Approval",
      ],
      openProjectStatusFilter: null,
      Statuses: [
        { text: "Not Started", value: 5 },
        { text: "Awaiting Install", value: 4 },
        { text: "Issue", value: 1 },
        { text: "Complete", value: 2 },
        { text: "Complete - Cubo Approved", value: 3 },
      ],
      duplicateProjectSource: null,
      duplicateProjectSupplier: null,
      createNewProject: false,
      tooltips: false,
      tooltipsDisabled: false,
      customerToRetrieve: null,
      today: moment().format("YYYY-MM-DD"),
      updateRegV: null,
      snackbar: false,
      snackbarMessage: "",
    };
  },
  created() {
    if (this.customer === "null") {
      return;
    }
    this.$store.dispatch("fetchCustomers");
    // fireStore.collection('Customers').get()
    // .then(snap => {
    //   snap.forEach(doc => {
    //     this.customerList.push({key: doc.id, name: doc.data().customerName, shortName: doc.data().shortName})
    //     this.customerManagement[doc.id] = {BDM: doc.data().BDM, DBAM: doc.data().DeskManager}
    //   })
    //   this.customerList.sort(function (a, b) {
    //     const aName = a.name.toLowerCase()
    //     const bName = b.name.toLowerCase()
    //     return aName < bName ? -1 : aName > bName ? 1 : 0
    //   })
    // })
    this.getEngineers();
    this.getProjects();
    this.getContacts();
    this.getProjectOrdering();
    this.getProjectManagers();
    this.$store.dispatch("fetchSuppliers");
    this.$store.dispatch("fetchDeviceTypes");
    this.$store.dispatch("fetchProductTypes");
    this.$store.dispatch("fetchDevices");
  },
  mounted() {
    if (this.customer !== undefined) {
      if (this.customer === "null") {
        this.$router.push("/Project");
      } else {
        this.activeCustomer = this.customer;
      }
    }
    if (this.job && this.job.indexOf("p:") === -1) {
      this.viewJob(this.job);
    } else if (this.job && this.job.indexOf("p:") !== -1) {
      this.search = this.job.substring(2);
      this.searchProject = this.job.substring(2);
    }
    this.defaultProject.Date = null;
    this.editedProject.Date = null;

    // this.projectDateDialog = moment().format('YYYY-MM-DD')
  },
  computed: {
    customerList() {
      return this.$store.getters.customers;
    },
    templates() {
      return this.$store.getters.deviceTypes;
    },
    products() {
      return this.$store.getters.productTypes;
    },
    devices() {
      return this.$store.getters.devices;
    },
    customerManagement() {
      let customerManagement = {};
      for (let customer of this.customerList) {
        customerManagement[customer.key] = {
          BDM: customer.BDM,
          DBAM: customer.DeskManager,
        };
      }
      return customerManagement;
    },
    projectHeaders() {
      let arr = [
        {
          text: "Project ID",
          value: "projectId",
          align: "left",
        },
        {
          text: "QNQ Number",
          value: "qnq",
          align: "left",
        },
        {
          text: "Customer",
          value: "Customer",
          align: "left",
        },
        {
          text: "Created Date",
          value: "CreatedDate",
          align: "left",
        },
        {
          text: "BDM/DBAM",
          value: "BDM",
          align: "left",
        },
        {
          text: "Project Status",
          value: "Status",
          align: "left",
        },
        {
          text: "Project Manager(s)",
          value: "manager",
          align: "left",
        },
        {
          text: "Priority",
          value: "priorityText",
          align: "left",
        },
        {
          text: "Priority Number",
          align: "left",
          value: "prioritySortOrder",
        },
        {
          text: "Actions",
          value: "actions",
          align: "right",
          sortable: false,
        },
      ];
      if (this.activeCustomer !== null) {
        arr[4] = {
          text: "Supplier",
          value: "Supplier",
          align: "left",
        };
        arr.splice(2, 1);
      }
      //  else {
      //   arr.splice(6, 1)
      // }
      return arr;
    },
    filteredJobs() {
      this.tableLoading = true;
      if (this.vehicles.length < 1) {
        return [];
      }
      let jobs = JSON.parse(JSON.stringify(this.jobs));
      let filteredJobs = [];
      for (const job in jobs) {
        if (jobs.hasOwnProperty(job)) {
          let element = jobs[job];
          if (this.activeCustomer !== null) {
            if (this.activeCustomer !== element.Customer) {
              continue;
            }
          }
          let addJob = false;
          if (this.statusFilter.length < 1) {
            addJob = true;
          } else if (
            this.statusFilter.indexOf(3) !== -1 &&
            element.Status === 3
          ) {
            addJob = true;
          } else if (
            this.statusFilter.indexOf(2) !== -1 &&
            element.Status === 2
          ) {
            addJob = true;
          } else if (
            this.statusFilter.indexOf(1) !== -1 &&
            element.Status === 1
          ) {
            addJob = true;
          } else if (
            this.statusFilter.indexOf(4) !== -1 &&
            element.Allocated &&
            (!element.Status || element.Status < 1)
          ) {
            addJob = true;
          } else if (
            this.statusFilter.indexOf(5) !== -1 &&
            !element.Allocated &&
            (!element.Status || element.Status < 1)
          ) {
            addJob = true;
          } else {
            addJob = false;
          }

          if (addJob === true) {
            element.VehicleYear = "";
            element.VehicleMake = "";
            element.VehicleModel = "";
            element.Engineer = this.engineerNameFromID(element.Allocated);
            if (element.Vehicle) {
              const index = this.vehicles.findIndex((vehicle) => {
                return vehicle.key === element.Vehicle;
              });
              if (index > -1) {
                if (
                  this.vehicles[index]["VehicleReg"] &&
                  this.vehicles[index]["VehicleReg"].length > 0
                ) {
                  element.Vehicle = this.vehicles[index]["VehicleReg"];
                } else {
                  element.Vehicle = this.vehicles[index]["friendlyId"];
                }
                element.VehicleYear = this.vehicles[index]["Year"];
                element.VehicleMake = this.vehicles[index]["Make"];
                element.VehicleModel = this.vehicles[index]["Model"];
                element.VehicleSite = this.vehicles[index]["Site"];
              } else {
                element.Vehicle = "";
              }
            } else {
              element.Vehicle = element.vehReg;
            }
            // element.Vehicle = element.Vehicle ? this.vehicleRegFromID(element.Vehicle) : element.vehReg
            filteredJobs.push(element);
          }
        }
      }
      this.tableLoading = false;
      return filteredJobs;
    },
    filteredProjects() {
      let projects = [];

      for (const project in this.projects) {
        const element = this.projects[project];
        if (
          this.activeCustomer !== null &&
          this.activeCustomer !== element.Customer
        ) {
          continue;
        }
        if (this.activeManager !== "") {
          if (typeof element.manager !== "string") {
            continue;
          }
          if (
            !element.manager
              .toLowerCase()
              .includes(this.activeManager.toLowerCase())
          ) {
            continue;
          }
        }

        if (element.priority == 1) {
          element.priorityText = "High";
        } else if (element.priority == 3) {
          element.priorityText = "Low";
        } else {
          element.priority = 2;
          element.priorityText = "Medium";
        }
        if (this.projectOrdering && element.projectId) {
          element.priorityOrder =
            this.projectOrdering.indexOf(element.projectId) !== -1
              ? this.projectOrdering.indexOf(element.projectId) + 1
              : null;
          element.prioritySortOrder =
            this.projectOrdering.indexOf(element.projectId) !== -1
              ? this.projectOrdering.indexOf(element.projectId) + 1
              : 10000000000000000;
        }

        if (this.customerManagement[element.Customer]) {
          let BDM = this.customerManagement[element.Customer].BDM || "";
          let DBAM = this.customerManagement[element.Customer].DBAM || "";
          element.BDM =
            BDM + (DBAM.length > 1 && BDM.length > 1 ? " / " : " ") + DBAM;
        }

        if (
          this.projectTypeFilter !== "0" &&
          !(
            this.projectTypeFilter === "sc" &&
            element.projectId.startsWith("SC")
          ) &&
          !(this.projectTypeFilter === "p" && element.projectId.startsWith("P"))
        ) {
          continue;
        }

        if (this.projectStatusFilter.length < 1) {
          projects.push(element);
        } else if (
          this.projectStatusFilter.indexOf("In Progress") !== -1 &&
          element.Status === "In Progress"
        ) {
          projects.push(element);
        } else if (
          this.projectStatusFilter.indexOf("In Progress HW setup") !== -1 &&
          element.Status === "In Progress HW setup"
        ) {
          projects.push(element);
        } else if (
          this.projectStatusFilter.indexOf("In Progress Shipped") !== -1 &&
          element.Status === "In Progress Shipped"
        ) {
          projects.push(element);
        } else if (
          this.projectStatusFilter.indexOf("Install In Progress") !== -1 &&
          element.Status === "Install In Progress"
        ) {
          projects.push(element);
        } else if (
          this.projectStatusFilter.indexOf("Complete") !== -1 &&
          element.Status === "Complete"
        ) {
          projects.push(element);
        } else if (
          this.projectStatusFilter.indexOf("Cancelled") !== -1 &&
          element.Status === "Cancelled"
        ) {
          projects.push(element);
        } else if (
          this.projectStatusFilter.indexOf("In Query") !== -1 &&
          element.Status === "In Query"
        ) {
          projects.push(element);
        } else if (
          this.projectStatusFilter.indexOf("CTN Sheet Approved") !== -1 &&
          element.Status === "CTN Sheet Approved"
        ) {
          projects.push(element);
        } else if (
          this.projectStatusFilter.indexOf("Awaiting Approval") !== -1 &&
          (!element.Status || element.Status === "Awaiting Approval")
        ) {
          projects.push(element);
        }
      }

      return projects;
    },
    filteredCustomerProjects() {
      let projects = this.projects;
      if (this.activeCustomer !== null) {
        projects = projects.filter((i) => this.activeCustomer === i.Customer);
      }
      return projects;
    },
    filteredCustomerProjectsByEditedCustomer() {
      let projects = this.projects;
      if (this.editedItem.Customer) {
        projects = projects.filter(
          (i) => this.editedItem.Customer === i.Customer
        );
      }
      return projects;
    },
    filteredOpenProjects() {
      return this.projects.filter((i) => {
        if (!this.openProjectStatusFilter) {
          return true;
        } else if (
          this.openProjectStatusFilter === "In Progress" &&
          i.Status === "In Progress"
        ) {
          return true;
        } else if (
          this.openProjectStatusFilter === "In Progress HW setup" &&
          i.Status === "In Progress HW setup"
        ) {
          return true;
        } else if (
          this.openProjectStatusFilter === "In Progress Shipped" &&
          i.Status === "In Progress Shipped"
        ) {
          return true;
        } else if (
          this.openProjectStatusFilter === "Install In Progress" &&
          i.Status === "Install In Progress"
        ) {
          return true;
        } else if (
          this.openProjectStatusFilter === "Complete" &&
          i.Status === "Complete"
        ) {
          return true;
        } else if (
          this.openProjectStatusFilter === "Cancelled" &&
          i.Status === "Cancelled"
        ) {
          return true;
        } else if (
          this.openProjectStatusFilter === "In Query" &&
          i.Status === "In Query"
        ) {
          return true;
        } else if (
          this.openProjectStatusFilter === "CTN Sheet Approved" &&
          i.Status === "CTN Sheet Approved"
        ) {
          return true;
        } else if (
          this.openProjectStatusFilter === "Awaiting Approval" &&
          (i.Status === undefined || i.Status === null)
        ) {
          return true;
        } else {
          return false;
        }
      });
    },
    formTitle() {
      return this.editedIndex === -1 ? "New Job" : "Edit Job";
    },
    projectFormTitle() {
      return this.editedProjectIndex === -1 ? "New Project" : "Edit Project";
    },
    suppliers() {
      return this.$store.getters.suppliers;
    },
    currentJobNo() {
      if (this.currentJob) {
        const index = this.jobs.findIndex((job) => {
          return this.currentJob === job.id;
        });
        if (index > -1) {
          return this.jobs[index]["JobNo"];
        } else {
          return null;
        }
      } else {
        return null;
      }
    },
    projectsObj() {
      let projectsObj = {};
      for (let project in this.projects) {
        projectsObj[this.projects[project].projectId] = this.projects[project];
      }
      return projectsObj;
    },
    supplierSelectEngineers() {
      let arr = [];
      this.engineers.forEach((engineer) => {
        if (engineer.supplier === this.supplierSelect) {
          arr.push(engineer);
        }
      });
      return arr;
    },
    assignToSupplierEngineers() {
      let arr = [];
      this.engineers.forEach((engineer) => {
        if (engineer.supplier === this.assignToSupplier) {
          arr.push(engineer);
        }
      });
      return arr;
    },
    selectedSameProject() {
      let result = true;
      if (this.selected.length === 0) {
        return true;
      }
      let currentProject = this.selected[0]["Project"];
      if (this.selected.length > 1) {
        for (let obj of this.selected) {
          if (currentProject !== obj.Project) {
            result = false;
            break;
          }
        }
      }
      if (result) {
        const index = this.projects.findIndex((project) => {
          return project.projectId === currentProject;
        });
        if (index > -1 && this.projects[index]["Supplier"]) {
          this.assignToSupplier = this.projects[index]["Supplier"];
        } else {
          this.assignToSupplier = null;
        }
      }
      return result;
    },
  },
  watch: {
    fab(val) {
      this.tooltips = false;
      this.tooltipsDisabled = false;
      val &&
        setTimeout(() => {
          this.tooltips = true;
          this.$nextTick(() => {
            this.tooltipsDisabled = true;
          });
        }, 250);
    },
    mergeJobsDialog(val) {
      if (!val) {
        this.getJobs();
      }
    },
    selected(val) {
      this.tooltips = false;
      this.tooltipsDisabled = false;
      this.mergeJobsDialog = false;
      val &&
        this.fab &&
        setTimeout(() => {
          this.tooltips = true;
          this.$nextTick(() => {
            this.tooltipsDisabled = true;
          });
        }, 450);
    },
    vehicleSelect: function (val) {
      if (val === null) {
        this.editedItem.Vehicle = null;
        return;
      }
      if (typeof val === "string") {
        this.editedItem.vehReg = val;
      } else {
        this.editedItem.vehReg = val.VehicleReg;
      }
      const index = this.vehicles.findIndex((vehicle) => {
        return vehicle.VehicleReg === this.editedItem.vehReg;
      });
      if (index > -1) {
        this.editedItem.Vehicle = this.vehicles[index]["key"];
      } else {
        this.editedItem.Vehicle = null;
      }
    },
    selectProducts: function (val) {
      console.log("changed products");
      this.editedItem.products = val;
      let productExtras = [];
      let productExtraKeys = [];
      // let keysToDelete = []
      this.editedItem.templateExtras = [];
      const allProducts = this.products;
      const products = this.editedItem.products;
      if (products) {
        products.forEach((v) => {
          // Stores the extras which match the currently selected products in the data model. Can then be queried to make sure we only show the correct extras in the form etc.
          const arrayId = allProducts.findIndex((obj) => {
            return obj.id === v;
          });
          if (
            allProducts[arrayId] !== undefined &&
            allProducts[arrayId]["extras"] !== undefined &&
            allProducts[arrayId]["extras"] !== null &&
            allProducts[arrayId]["extras"] !== ""
          ) {
            allProducts[arrayId]["extras"].forEach((extra) => {
              extra["id"] = allProducts[arrayId]["id"];
              productExtras.push(extra);
              productExtraKeys.push(extra.name);
            });
          }

          let templateDetails = [];
          if (allProducts[arrayId] && allProducts[arrayId].devices) {
            for (let template of allProducts[arrayId].devices) {
              let device = this.templates.find((t) => {
                return template === t.id;
              });
              device.deviceDetails = this.devices.find((d) => {
                return (
                  d.type === device.id && d.vehicle === this.editedItem.Vehicle
                );
              });
              templateDetails.push(device);
            }
            this.editedItem.templateExtras.push({
              product: allProducts[arrayId],
              templateDetails: templateDetails,
            });
          }
        });
        // for (let key in this.editedItem.extras) {
        //   // Store names of extras which need to be deleted
        //   if (!productExtraKeys.includes(key)) {
        //     keysToDelete.push(key)
        //   }
        // }
        // keysToDelete.forEach((keyName) => {
        //   delete this.editedItem.extras[keyName]
        // })
        for (let key in productExtraKeys) {
          // IF value of extras hasn't been set, set the value to the default found in db
          let obj = {};
          if (this.editedItem.extras !== undefined) {
            if (!this.editedItem.extras.hasOwnProperty(productExtraKeys[key])) {
              var index = productExtras.findIndex((obj) => {
                return obj.name === productExtraKeys[key];
              });
              if (index === -1) {
                obj[productExtraKeys[key]] = "";
              } else {
                obj[productExtraKeys[key]] = productExtras[key]["default"];
              }
              Object.assign(this.editedItem.extras, obj);
            }
          } else {
            // this.editedItem.extras = {}
            obj[productExtraKeys[key]] = "";
            Object.assign(this.editedItem.extras, obj);
          }
          if (products.indexOf("IGVjrrgemWhH8zki8yBZ") > -1) {
            const vehicleObj = this.vehicles.find((vehicle) => {
              return vehicle.key === this.editedItem.Vehicle;
            });
            this.editedItem.extras["[Tacho Smart] VU Part Number"] =
              vehicleObj && vehicleObj.VUPartNo ? vehicleObj.VUPartNo : "";
          }
        }

        this.editedItem.productExtras = productExtraKeys;
        this.productExtras = productExtras;
        console.log(productExtras);
        // this.editedItem.extras = this.editedItem.extras
      }
    },
    activeCustomer: function (val) {
      this.activeCustomer = val === undefined ? null : val;
      this.editedItem.Customer = this.activeCustomer;
      this.defaultItem.Customer = this.activeCustomer;
      this.customerToRetrieve = this.activeCustomer;
      this.setProjectStates(val);
    },
    customerToRetrieve: function (val) {
      this.getSites();
      this.getVehicles();
      this.getJobs();
    },
    dialog(val) {
      if (!val) {
        this.close();
      }
    },
    // supplierSelect: function (val) {
    //   this.editedItem.Supplier = val
    //   this.getEngineers(val)
    // },
    // assignToSupplier: function (val) {
    //   this.getEngineers(val)
    // }
  },
  filters: {
    formatDate(val) {
      return val !== undefined && val !== null
        ? moment(val).format("DD/MM/YYYY")
        : null;
    },
    arrayToString(val) {
      let str = "";
      if (val && typeof val === "object") {
        val.forEach((arrayVal) => {
          str += arrayVal + ", ";
        });
        str = str.substr(0, str.length - 2);
      }
      return str;
    },
    jobStatus(Status, Allocated) {
      if (Status === 3) {
        return "Complete - Cubo Approved";
      } else if (Status === 2) {
        return "Complete";
      } else if (Status === 1) {
        return "Issue";
      } else {
        if (Allocated === "" || Allocated === null || Allocated === undefined) {
          return "Not Started";
        } else {
          return "Awaiting Install";
        }
      }
    },
  },
  methods: {
    expandProjectJob(job) {
      this.expandedProjectJob = job;
    },
    async saveChangedProducts() {
      for (let selectedIndex in this.selected) {
        this.selected[selectedIndex].products = this.allSelectedProducts;
        let productExtras = [];
        let productExtraKeys = [];
        // let keysToDelete = []
        this.selected[selectedIndex].templateExtras = [];
        const allProducts = this.products;
        const products = this.selected[selectedIndex].products;
        if (products) {
          products.forEach((v) => {
            // Stores the extras which match the currently selected products in the data model. Can then be queried to make sure we only show the correct extras in the form etc.
            const arrayId = allProducts.findIndex((obj) => {
              return obj.id === v;
            });
            if (
              allProducts[arrayId] !== undefined &&
              allProducts[arrayId]["extras"] !== undefined &&
              allProducts[arrayId]["extras"] !== null &&
              allProducts[arrayId]["extras"] !== ""
            ) {
              allProducts[arrayId]["extras"].forEach((extra) => {
                extra["id"] = allProducts[arrayId]["id"];
                productExtras.push(extra);
                productExtraKeys.push(extra.name);
              });
            }
            let templateDetails = [];
            if (allProducts[arrayId] && allProducts[arrayId].devices) {
              for (let template of allProducts[arrayId].devices) {
                let device = this.templates.find((t) => {
                  return template === t.id;
                });
                device.deviceDetails = this.devices.find((d) => {
                  return (
                    d.type === device.id &&
                    d.vehicle === this.selected[selectedIndex].Vehicle
                  );
                });
                templateDetails.push(device);
              }
              this.selected[selectedIndex].templateExtras.push({
                product: allProducts[arrayId],
                templateDetails: templateDetails,
              });
            }
          });
          for (let key in productExtraKeys) {
            // IF value of extras hasn't been set, set the value to the default found in db
            let obj = {};
            if (this.selected[selectedIndex].extras !== undefined) {
              if (
                !this.selected[selectedIndex].extras.hasOwnProperty(
                  productExtraKeys[key]
                )
              ) {
                var index = productExtras.findIndex((obj) => {
                  return obj.name === productExtraKeys[key];
                });
                if (index === -1) {
                  obj[productExtraKeys[key]] = "";
                } else {
                  obj[productExtraKeys[key]] = productExtras[key]["default"];
                }
                Object.assign(this.selected[selectedIndex].extras, obj);
              }
            } else {
              console.log(this.selected[selectedIndex].extras);
              // this.editedItem.extras = {}
              obj[productExtraKeys[key]] = "";
            }
            if (products.indexOf("IGVjrrgemWhH8zki8yBZ") > -1) {
              const vehicleObj = this.vehicles.find((vehicle) => {
                return vehicle.key === this.selected[selectedIndex].Vehicle;
              });
              this.selected[selectedIndex].extras[
                "[Tacho Smart] VU Part Number"
              ] = vehicleObj && vehicleObj.VUPartNo ? vehicleObj.VUPartNo : "";
            }
          }
          this.selected[selectedIndex].productExtras = productExtraKeys;
          this.productExtras = productExtras;
          console.log(productExtras);
        }
        this.savingSaveJob = true;
        let sortedTemplates = [];
        let deviceArray = [];
        let jobLookupIds = [];
        this.templates.forEach((template) => {
          for (let t of this.selected[selectedIndex].templateExtras) {
            if (t.product.devices.includes(template.id)) {
              sortedTemplates.push(template.id);
            }
            for (let d of t.templateDetails) {
              if (d.deviceDetails) {
                deviceArray.push({
                  product: t.product.id,
                  device: d.deviceDetails.id,
                });
                if (d.deviceDetails.IMEI) {
                  jobLookupIds.push({
                    IMEI: d.deviceDetails.IMEI,
                    template: d.deviceDetails.type,
                  });
                }
                if (d.deviceDetails["DVR ID Number"]) {
                  jobLookupIds.push({
                    DVR: d.deviceDetails["DVR ID Number"],
                    template: d.deviceDetails.type,
                  });
                }
              }
            }
          }
        });
        if (
          sortedTemplates.indexOf("preCheck") === -1 &&
          sortedTemplates.indexOf("5oJKuVseN0PkmvZeDl0J") === -1
        ) {
          sortedTemplates.unshift("preCheck");
        }
        if (
          sortedTemplates.indexOf("Je2nQqSdazW25XWSGkt3") === -1 &&
          sortedTemplates.indexOf("5oJKuVseN0PkmvZeDl0J") === -1
        ) {
          sortedTemplates.push("Je2nQqSdazW25XWSGkt3");
        }
        jobLookupIds = jobLookupIds.filter((obj, pos, arr) => {
          return (
            arr.map((mapObj) => mapObj["template"]).indexOf(obj["template"]) ===
            pos
          );
        });
        this.selected[selectedIndex].templates = sortedTemplates.filter(
          (item, pos) => sortedTemplates.indexOf(item) === pos
        );
        let obj = this.selected[selectedIndex];
        obj.jobLookupIds = jobLookupIds;
        if (obj.extras) {
          for (let extra in obj.extras) {
            if (!obj.extras[extra]) {
              obj.extras[extra] = "";
            }
          }
        }
        delete obj["productExtras"];
        delete obj["templateExtras"];
        try {
          let response = await fireStore
            .collection("Jobs")
            .doc(this.selected[selectedIndex].id)
            .update({
              products: obj.products,
              templates: obj.templates,
              extras: obj.extras,
              newMethod: true,
            });
          updateAuditLog(
            "changeProducts",
            "projects",
            this.userData,
            this.selected[selectedIndex].JobNo,
            this.selected[selectedIndex].Project,
            "edited job",
            null,
            this.activeCustomer
          );
          console.log(response);
        } catch (error) {
          this.saveJobFailed = true;
          console.log(error);
        }
      }
      this.savingSaveJob = false;
      this.changeProductsDialog = false;
      this.getJobs();
    },
    getProjectOrdering() {
      fireStore
        .collection("ProjectOrdering")
        .doc("order")
        .get()
        .then((doc) => {
          this.projectOrdering = doc.data().array || [];
        });
    },
    changeProducts() {
      this.allProductsMatch = true;
      this.allSelectedProducts = [];
      let template = this.selected[0].products
        ? this.selected[0].products.slice().sort()
        : this.selected[0].templates.slice().sort();
      for (let job of this.selected) {
        let match = false;
        if (!job.newMethod) {
          for (let product of job.templates) {
            if (this.allSelectedProducts.indexOf(product) === -1) {
              this.allSelectedProducts.push(product);
            }
          }
          match =
            job.templates.length === template.length &&
            job.templates
              .slice()
              .sort()
              .every(function (value, index) {
                return value === template[index];
              });
        } else {
          for (let product of job.products) {
            if (this.allSelectedProducts.indexOf(product) === -1) {
              this.allSelectedProducts.push(product);
            }
          }
          match =
            job.products.length === template.length &&
            job.products
              .slice()
              .sort()
              .every(function (value, index) {
                return value === template[index];
              });
        }
        if (!match) {
          this.allProductsMatch = false;
        }
      }
      this.changeProductsDialog = true;
    },
    updateReg(job) {
      const vehIndex = this.vehicles.findIndex((vehicle) => {
        if (job["Vehicle"]) {
          return vehicle.VehicleReg === job["Vehicle"];
        }
      });
      let vehDoc;
      if (vehIndex > -1) {
        vehDoc = this.vehicles[vehIndex]["key"];
        const vehRef = fireStore.collection("Vehicles").doc(vehDoc);
        vehRef.update({ VehicleReg: this.updateRegV });
        this.getVehicles();
      }
    },
    forceUpdate() {
      this.$forceUpdate();
    },
    async printSelectedJobs() {
      var mywindow = window.open("", "PRINT");

      mywindow.document.write(
        '<html><head><title>Install Report</title><link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/materialize/1.0.0/css/materialize.min.css"><style>@media print{#hidePrint{display:none;}}</style>'
      );
      mywindow.document.write("</head><body>");
      mywindow.document.write(
        '<div style="margin-top:10px;" id="hidePrint">Once all images are downloaded click print --> <button class="btn" onclick="window.print();window.close();">Print</button></div>'
      );
      mywindow.document.write(
        '<img style="margin:3px;" width="200" src="../static/img/Cubo-Logo.png" />'
      );

      for (let job of this.selected) {
        let printJob = {};
        let snap = await fireStore
          .collection("Jobs")
          .doc(job.id)
          .collection("CompletedJobs")
          .get();
        for (let doc of snap.docs) {
          printJob = doc.data();
        }
        if (Object.keys(printJob).length < 1) {
          continue;
        }
        if (printJob.vehicle !== undefined) {
          mywindow.document.write("<h4>Vehicle: " + printJob.vehicle + "</h4>");
        } else if (printJob.reg !== undefined) {
          mywindow.document.write("<h4>Vehicle: " + printJob.reg + "</h4>");
        } else {
          mywindow.document.write("<h4>Job: " + printJob.JobNo + "</h4>");
        }
        if (printJob.Date !== undefined) {
          mywindow.document.write(
            "<h4>Date: " + moment(printJob.Date).format("DD/MM/YYYY") + "</h4>"
          );
        }
        for (let item in printJob.template) {
          mywindow.document.write(
            "<h6>" + printJob.template[item].name + "</h6>"
          );
          for (let subitem in printJob.template[item].items) {
            let subitemDetail = printJob.template[item].items[subitem];
            if (subitemDetail.image.length > 0) {
              mywindow.document.write(
                '<img style="margin:3px;" height="250" src="' +
                  subitemDetail.image +
                  '" />'
              );
            } else if (subitemDetail.note.length > 0) {
              mywindow.document.write(
                '<div style="width:200px; display:inline-block;"><span style="margin:3px;">' +
                  subitemDetail.text +
                  ": <strong>" +
                  subitemDetail.note +
                  "</strong></span></div>"
              );
            }
          }
        }
      }
      mywindow.document.close(); // necessary for IE >= 10
      mywindow.focus(); // necessary for IE >= 10*/

      return true;
    },
    copyJobsData() {
      let exportString = "";
      let selectedJobs = this.selected;
      console.log(selectedJobs);
      if (selectedJobs.length < 1) {
        console.log("Entered in selected job length < 1");
        selectedJobs = this.$refs.jobsTable.filteredItems;
      }
      for (let header of this.headers) {
        if (header.text !== "Actions") {
          exportString += header.text + "\t";
        }
      }
      exportString += "\n";
      for (let job of selectedJobs) {
        for (let header of this.headers) {
          if (header.text !== "Actions") {
            if (header.text === "Status") {
              exportString +=
                (this.$options.filters.jobStatus(job.Status, job.Allocated) ||
                  "") + "\t";
            } else if (header.text === "Engineer Cost") {
              exportString += (job.currency || "£") + (job.cost || 0) + "\t";
            } else {
              exportString += (job[header.value] || "") + "\t";
            }
          }
        }
        exportString += "\n";
      }
      navigator.clipboard.writeText(exportString).then(() => {
        console.log(exportString);
        this.snackbarMessage = "Copied to clipboard successfully";
        this.snackbar = true;
        setTimeout(() => {
          this.snackbar = false;
          this.snackbarMessage = "";
        }, 3000);
      });
    },
    updateCosts() {
      this.updatingCosts = true;
      let batch = fireStore.batch();
      for (let job of this.selected) {
        const jobRef = fireStore.collection("Jobs").doc(job.id);
        batch.update(jobRef, {
          cost: this.bulkUpdateCost,
          currency: this.bulkUpdateCurrency,
        });
        updateAuditLog(
          "update",
          "projects",
          this.userData,
          job.JobNo,
          job.Project,
          "updated jobs engineer costs",
          { cost: this.bulkUpdateCost, currency: this.bulkUpdateCurrency },
          job.Customer
        );
      }
      batch
        .commit()
        .then(() => {
          this.updatingCosts = false;
          this.bulkUpdateCostDialog = false;
          this.getJobs();
        })
        .catch(() => {
          this.updatingCosts = false;
        });
    },
    refreshData() {
      this.getProjects();
      this.getSites();
      this.getVehicles();
      this.getJobs();
    },
    getTemplates() {
      fireStore
        .collection("deviceType")
        .orderBy("Index")
        .get()
        .then((snap) => {
          snap.forEach((doc) => {
            let data = doc.data();
            data.id = doc.id;
            this.templates.push(data);
          });
        });
    },
    deleteFile(file) {
      storage
        .refFromURL(file.downloadUrl)
        .delete()
        .then(() => {
          this.editedProject.attachedDocs.splice(
            this.editedProject.attachedDocs.indexOf(file),
            1
          );
          this.saveProject(false);
        });
    },
    sendBookingEmail() {
      this.emailError = "";
      for (let email in this.bookingEmailAddress) {
        if (
          /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
            this.bookingEmailAddress[email]
          ) === false
        ) {
          this.emailError = "Invalid Email Address";
          return;
        }
      }
      this.sendingEmail = true;
      const contacts = [];
      for (let contact in this.editedProject.contacts) {
        contacts.push(
          JSON.parse(
            JSON.stringify(
              this.contacts.find((singleContact) => {
                return (
                  singleContact.key == this.editedProject.contacts[contact]
                );
              })
            )
          )
        );
      }
      const emailObj = {
        emails: this.bookingEmailAddress,
        projectID: this.editedProject.projectId,
        customer: this.customerList.find((customer) => {
          return customer.key == this.activeCustomer;
        }).name,
        supplier: this.suppliers.find((thisSupplier) => {
          return thisSupplier.key === this.editedProject.Supplier;
        }).name,
        contacts: contacts,
        sites: JSON.parse(JSON.stringify(this.sites)),
        cost: this.editedProject.supplierCost,
        jobs: JSON.parse(
          JSON.stringify(
            this.filteredJobs.filter((job) => {
              return job.Project == this.editedProject.projectId;
            })
          )
        ),
        templates: Object.assign(
          {},
          ...this.templates.map((obj) => {
            var rObj = {};
            rObj[obj.key] = obj.name;
            return rObj;
          })
        ),
      };

      fetch(
        "https://us-central1-engineer-ae17e.cloudfunctions.net/sendBookingEmail",
        {
          headers: { "content-type": "application/json; charset=UTF-8" },
          method: "POST",
          body: JSON.stringify(emailObj),
        },
        { mode: "cors" }
      )
        .then((data) => {
          return data.json();
        })
        .then((res) => {
          if (res.status === "success") {
            this.emailSuccess = "Email(s) have been sent";
            setTimeout(() => {
              this.emailSuccess = "";
              this.bookingEmailAddress = [];
            }, 3000);
          } else {
            this.emailError = "Email(s) have failed";
          }
          this.sendingEmail = false;
        })
        .catch((error) => console.log(error));
    },
    getContacts() {
      this.contacts = [];
      fireStore
        .collection("Contacts")
        .get()
        .then((snap) => {
          this.selectedContacts = [];
          snap.forEach((doc) => {
            this.contacts.push({ ...doc.data(), key: doc.id });
            if (
              doc.data().Site &&
              doc.data().Site.indexOf(this.editedSite) !== -1
            ) {
              this.selectedContacts.push(doc.id);
            }
          });
        });
    },
    getProjectManagers() {
      this.projectManagers = [];
      fireStore
        .collection("Users")
        .where("Company", "==", "5BepIUcIKKTJbKBzjP9x")
        .get()
        .then((snap) => {
          snap.forEach((doc) => {
            this.projectManagers.push(doc.data());
          });
        });
    },
    attachFile(f) {
      let projectId = this.editedProject.projectId;
      let promises = [];
      let downloadUrls = [];
      let fileObj = { Progress: 0, State: 0, files: f };
      const storageRef = storage.ref("OrderDocs/Project" + projectId);
      for (const file of fileObj.files) {
        promises.push(
          new Promise((resolve, reject) => {
            let uploadTask = storageRef.child(file.name).put(file);
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                // Observe state change events such as progress, pause, and resume
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                var progress =
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                fileObj["Progress"] = progress;
                this.fileProgress = progress;
              },
              (error) => {
                // Handle unsuccessful uploads
                fileObj["State"] = 4;
                reject(error);
              },
              () => {
                // Handle successful uploads on complete
                fileObj["Progress"] = 100;
                this.fileProgress = 100;
                fileObj["State"] = 3;
                uploadTask.snapshot.ref
                  .getDownloadURL()
                  .then(function (downloadURL) {
                    downloadUrls.push({
                      downloadUrl: downloadURL,
                      name: file.name,
                    });
                    resolve();
                  });
              }
            );
          })
        );
      }

      Promise.all(promises)
        .then((val) => {
          const index = this.projects.findIndex((project) => {
            return project.projectId === projectId;
          });
          console.log(index);
          console.log("download urls", downloadUrls);
          if (this.projects[index].attachedDocs === undefined) {
            this.projects[index].attachedDocs = [];
          }
          this.projects[index].attachedDocs.push(...downloadUrls);
          this.$forceUpdate();
          fireStore.collection("Projects").doc(this.editedProject.id).update({
            attachedDocs: this.projects[index].attachedDocs,
          });
          updateAuditLog(
            "update",
            "projects",
            this.userData,
            null,
            projectId,
            "edited Attached docs",
            null,
            this.activeCustomer
          );
        })
        .catch((error) => {
          console.log(error);
        });
    },
    attachCompletedFile(f) {
      let promises = [];
      let downloadUrls = [];
      let fileObj = { Progress: 0, State: 0, files: f };
      const storageRef = storage.ref("CompletedDocs/" + this.currentJob);

      for (const file of fileObj.files) {
        console.log(file);
        promises.push(
          new Promise((resolve, reject) => {
            let uploadTask = storageRef.child(file.name).put(file);
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                // Observe state change events such as progress, pause, and resume
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                var progress =
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                fileObj["Progress"] = progress;
                this.fileProgress = progress;
              },
              (error) => {
                // Handle unsuccessful uploads
                fileObj["State"] = 4;
                reject(error);
              },
              () => {
                // Handle successful uploads on complete
                fileObj["Progress"] = 100;
                this.fileProgress = 100;
                fileObj["State"] = 3;
                uploadTask.snapshot.ref
                  .getDownloadURL()
                  .then(function (downloadURL) {
                    downloadUrls.push({
                      downloadUrl: downloadURL,
                      name: file.name,
                    });
                    resolve();
                  });
              }
            );
          })
        );
      }

      Promise.all(promises)
        .then((val) => {
          const index = this.jobs.findIndex((job) => {
            return job.id === this.currentJob;
          });
          console.log(index);
          console.log(val);
          if (!this.jobs[index].completedDocs) {
            this.jobs[index].completedDocs = [];
          }
          console.log("CURRRENT JOB -", this.currentJob);
          this.jobs[index].completedDocs.push(...downloadUrls);
          fireStore.collection("Jobs").doc(this.currentJob).update({
            completedDocs: this.jobs[index].completedDocs,
          });
          updateAuditLog(
            "update",
            "projects",
            this.userData,
            this.jobs[index].JobNo,
            this.jobs[index].Project,
            "attached completed document",
            null,
            this.activeCustomer
          );
          this.$forceUpdate();
        })
        .catch((error) => {
          console.log(error);
        });
    },
    expandedJobs(projectID) {
      let arr = [];
      this.jobs.forEach((job) => {
        if (job.Project === projectID) {
          arr.push(job);
        }
      });
      return arr;
    },
    projectSummary(projectID) {
      let summary = {};
      this.jobs.forEach((job) => {
        if (job.Project === projectID) {
          console.log(job.templates);
          job.templates.forEach((element) => {
            if (element !== "preCheck" && element !== "Je2nQqSdazW25XWSGkt3") {
              if (summary[element]) {
                summary[element]++;
              } else {
                summary[element] = 1;
              }
            }
          });
        }
      });
      console.log(summary);
      return summary;
    },
    getNewProjectId() {
      this.gettingNewProjectId = true;
      this.newProject = true;
      const vm = this;
      const index = this.customerList.findIndex(function (obj) {
        return obj.key === vm.activeCustomer;
      });

      const shortName = this.customerList[index]["shortName"];
      let projectId = "";
      let projectIds = [];
      fireStore
        .collection("Projects")
        .where("Customer", "==", this.activeCustomer)
        .get()
        .then((docRef) => {
          docRef.forEach((doc) => {
            let projId = doc.data().projectId;
            if (projId) {
              projId = projId.replace(/\D/g, "");
              projectIds.push(projId);
            }
          });
          projectIds = projectIds.sort();
          projectIds = projectIds.reverse();
          if (projectIds.length === 0) {
            projectIds.push(0);
          }
          projectId = projectIds[0];
          this.editedProject.projectId =
            "P-" + shortName + this.zeroFill(parseInt(projectId) + 1, 4);
          const obj = {
            projectId: this.editedProject.projectId,
            Customer: this.activeCustomer,
          };
          fireStore
            .collection("Projects")
            .add(obj)
            .then((docRef) => {
              updateAuditLog(
                "add",
                "projects",
                this.userData,
                null,
                obj.projectId,
                "created new project",
                null,
                this.activeCustomer
              );
              const newProject = {
                ...obj,
                id: docRef.id,
              };
              this.editedProject.id = docRef.id;
              this.projects.push(newProject);
              this.gettingNewProjectId = false;
            })
            .catch((error) => {
              console.log(error);
              this.gettingNewProjectId = false;
            });
        });
    },
    setProjectStates(activeCustomer) {
      // if (activeCustomer) {
      this.totalProjects = 0;
      this.projectStates = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      this.projects.forEach((data) => {
        if (
          data.Customer === activeCustomer ||
          activeCustomer === undefined ||
          activeCustomer === null
        ) {
          if (data.Status === "CTN Sheet Approved") {
            this.projectStates[2]++;
          } else if (data.Status === "In Query") {
            this.projectStates[1]++;
          } else if (data.Status === "In Progress") {
            this.projectStates[4]++;
          } else if (data.Status === "In Progress HW setup") {
            this.projectStates[5]++;
          } else if (data.Status === "In Progress Shipped") {
            this.projectStates[6]++;
          } else if (data.Status === "Install In Progress") {
            this.projectStates[7]++;
          } else if (data.Status === "Complete") {
            this.projectStates[8]++;
          } else if (data.Status === "Cancelled") {
            this.projectStates[3]++;
          } else {
            this.projectStates[0]++;
          }
          this.totalProjects++;
        }
      });
      // }
    },
    setJobStates(activeCustomer) {
      if (activeCustomer) {
        this.totalJobs = 0;
        this.states = [0, 0, 0, 0, 0];
        this.jobs.forEach((data) => {
          if (data.Customer === activeCustomer) {
            if (data.Status === 3) {
              this.states[4]++;
            } else if (data.Status === 2) {
              this.states[3]++;
            } else if (data.Status === 1) {
              this.states[2]++;
            } else {
              if (
                data.Allocated === "" ||
                data.Allocated === null ||
                data.Allocated === undefined
              ) {
                this.states[0]++;
              } else {
                this.states[1]++;
              }
            }
            this.totalJobs++;
          }
        });
      }
    },
    getProjects() {
      this.projectTableLoading = true;
      this.projects = [];
      let tempArray = [];
      if (this.includeArchive) {
        fireStore
          .collection("Projects")
          .get()
          .then((snap) => {
            snap.forEach((doc) => {
              let data = doc.data();
              if (data.notes !== undefined) {
                data.notes = data.notes.replace(/<br>/g, "\r");
              }
              data.id = doc.id;
              tempArray.push(data);
            });
            this.projects = tempArray;
            this.projectTableLoading = false;
            this.setProjectStates();
          });
      } else {
        fireStore
          .collection("Projects")
          .where("Status", "<", "Complete")
          .get()
          .then((snap) => {
            snap.forEach((doc) => {
              let data = doc.data();
              if (data.notes !== undefined) {
                data.notes = data.notes.replace(/<br>/g, "\r");
              }
              data.id = doc.id;
              tempArray.push(data);
            });
            fireStore
              .collection("Projects")
              .where("Status", ">", "Complete")
              .get()
              .then((snap) => {
                snap.forEach((doc) => {
                  let data = doc.data();
                  if (data.notes !== undefined) {
                    data.notes = data.notes.replace(/<br>/g, "\r");
                  }
                  data.id = doc.id;
                  tempArray.push(data);
                });
                this.projects = tempArray;
                this.projectTableLoading = false;
                this.setProjectStates();
              });
          });
      }
    },
    openProject(item) {
      console.log(item);
      window.open(`#/Project/${item.Customer}/p:${item.projectId}`);
    },
    getJobs() {
      if (this.customerToRetrieve === null) {
        return;
      }
      this.tableLoading = true;
      this.jobs = [];
      fireStore
        .collection("Jobs")
        .where("Customer", "==", this.customerToRetrieve)
        .get()
        .then((snap) => {
          snap.forEach((doc) => {
            let data = doc.data();
            data.id = doc.id;
            this.jobs.push(data);
          });
          this.setJobStates(this.customerToRetrieve);
          this.tableLoading = false;
        });
    },
    getSites() {
      if (this.customerToRetrieve === null) {
        return;
      }
      this.sites = [];
      if (this.customerToRetrieve !== null) {
        fireStore
          .collection("Sites")
          .where("Customer", "==", this.customerToRetrieve)
          .get()
          .then((snap) => {
            snap.forEach((doc) => {
              this.sites.push({ ...doc.data(), ...{ key: doc.id } });
            });
          });
      }
    },
    getEngineers(supplier) {
      this.engineers = [];
      fireStore
        .collection("Users")
        .where("Role", "in", [
          "Engineer",
          "Supplier",
          "Support and Engineer",
          "Admin and Engineer",
        ])
        .get()
        .then((snap) => {
          snap.forEach((doc) => {
            this.engineers.push({
              key: doc.data().UID,
              name: doc.data().Name,
              supplier: doc.data().Company,
            });
          });
        });
    },
    getVehicles() {
      if (this.customerToRetrieve === null) {
        return;
      }
      this.gettingVehicles = true;
      this.vehicles = [];
      if (this.customerToRetrieve !== null) {
        fireStore
          .collection("Vehicles")
          .where("Customer", "==", this.customerToRetrieve)
          .get()
          .then((snap) => {
            snap.forEach((doc) => {
              let data = doc.data();
              if (!data.VehicleReg) {
                data.VehicleReg = data.friendlyId;
              }
              this.vehicles.push({ ...data, key: doc.id });
            });
            this.gettingVehicles = false;
          })
          .catch(() => {
            this.gettingVehicles = false;
          });
      } else {
        this.gettingVehicles = false;
      }
    },
    viewJob(job) {
      this.currentJob = job;
      this.viewDialog = true;
    },
    issueJob(job) {
      this.currentJob = job;
      this.issueDialog = true;
      this.JobNotes =
        this.jobs.find((job) => {
          return job.id === this.currentJob;
        }).Notes || "";
    },
    issueCurrentJob() {
      this.issueingJob = true;
      const index = this.jobs.findIndex((job) => {
        return job.id === this.currentJob;
      });
      fireStore
        .collection("Jobs")
        .doc(this.currentJob)
        .update({ Notes: this.JobNotes, Status: 1 })
        .then(() => {
          updateAuditLog(
            "update",
            "projects",
            this.user,
            this.jobs[index].JobNo,
            this.jobs[index].Project,
            "flagged as issue",
            null,
            this.jobs[index].Customer
          );
          let obj = this.jobs[index];
          console.log(obj);
          obj.Status = 1;
          obj.Notes = this.JobNotes;
          this.jobs[index] = Object.assign({}, this.jobs[index], obj);
          this.setJobStates(this.activeCustomer);
          this.issueingJob = false;
          this.issueDialog = false;
        });
    },
    approveJob(job) {
      this.currentJob = job;
      const approvalVehicle = this.jobs.find((job) => {
        return job.id === this.currentJob;
      }).Vehicle;
      const approvalVehicleReg = this.vehicles.find((vehicle) => {
        return vehicle.key === approvalVehicle;
      });
      this.approvalReg = approvalVehicleReg.VehicleReg.toUpperCase().replace(
        /\s/g,
        ""
      );
      this.approveDialog = true;
      document.getElementById(this.$refs.btnAttachCompletedFile.id).value =
        null;
    },
    approveCurrentJob() {
      this.approvingJob = true;
      let batch = fireStore.batch();
      let vmJobs = this.jobs;
      const index = this.jobs.findIndex((job) => {
        return job.id === this.currentJob;
      });
      let installedDevices = vmJobs[index].finance || {};
      let completedDate =
        vmJobs[index].CompletedDate || moment().format("YYYY-MM-DD");
      for (let device in installedDevices) {
        if (typeof installedDevices[device] !== "string" && typeof installedDevices[device] !== "number") {
          installedDevices[device].installDate = completedDate;
          installedDevices[device].project = vmJobs[index].Project;
          installedDevices[device].job = vmJobs[index].id;
        }
      }
      // if (vmJobs[index].products) {
      //   vmJobs[index].products.forEach(template => {
      //     if (template !== 'preCheck') {
      //       installedDevices[template] = true
      //     }
      //   })
      // } else {
      //   vmJobs[index]['templates'].forEach(template => {
      //     if (template !== 'preCheck') {
      //       installedDevices[template] = true
      //     }
      //   })
      // }

      let devices = {};
      if (vmJobs[index]["extras"]) {
        devices = vmJobs[index]["extras"];
      }
      let obj = {
        devices: devices,
        installedDevices: installedDevices,
      };
      if (this.approvalReg && this.approvalReg.length > 0) {
        obj.VehicleReg = this.approvalReg;
      }
      const vehIndex = this.vehicles.findIndex((vehicle) => {
        if (vmJobs[index]["Vehicle"]) {
          return vehicle.key === vmJobs[index]["Vehicle"];
        } else if (vmJobs[index]["vehReg"]) {
          return vehicle.VehicleReg === vmJobs[index]["vehReg"];
        } else if (vmJobs[index]["reg"]) {
          return vehicle.VehicleReg === vmJobs[index]["reg"];
        }
      });
      let vehDoc;
      if (vehIndex > -1) {
        vehDoc = this.vehicles[vehIndex]["key"];
        const vehRef = fireStore.collection("Vehicles").doc(vehDoc);
        batch.set(vehRef, obj, { merge: true });
      }
      const jobRef = fireStore.collection("Jobs").doc(this.currentJob);
      let deviceArray = [];
      for (let template of Object.keys(installedDevices)) {
        if (
          this.products.find((p) => {
            return template === p.id;
          }) === undefined
        ) {
          continue;
        }
        let deviceKey = this.products.find((p) => {
          return template === p.id;
        }).devices[0];
        let device = this.templates.find((t) => {
          return deviceKey === t.id;
        });
        if (device) {
          device.deviceDetails = this.devices.find((d) => {
            return d.type === device.id && d.vehicle === vehDoc;
          });
          if (device.deviceDetails) {
            deviceArray.push(device.deviceDetails.id);
            const deviceRef = fireStore
              .collection("Devices")
              .doc(device.deviceDetails.id);
            batch.update(deviceRef, {
              InstallDate: completedDate,
              stockLocation: "Installed",
            });
          }
        }
      }
      batch.update(jobRef, {
        Status: 3,
        CompletedDate: completedDate,
        devicesInstalled: deviceArray,
      });
      batch
        .commit()
        .then(() => {
          let obj = this.jobs[index];
          obj.Status = 3;
          obj.CompletedDate = completedDate;
          this.jobs[index] = Object.assign({}, this.jobs[index], obj);
          this.setJobStates(this.activeCustomer);
          this.approvingJob = false;
          this.approveDialog = false;
          this.$forceUpdate();
          this.getVehicles();
          updateAuditLog(
            "update",
            "projects",
            this.userData,
            this.jobs[index].JobNo,
            this.jobs[index].Project,
            "approved job",
            null,
            this.activeCustomer
          );
          console.log("send email");
          let project =
            this.projects.find(
              (a) => a.projectId === this.jobs[index].Project
            ) || {};
          let customer = this.customerList.find(
            (a) => a.key === this.activeCustomer
          );
          let engineer = this.engineerNameFromID(obj.Allocated);
          let supplier = this.supplierNameFromID(project.Supplier);
          let vehicle = this.approvalReg;
          sendEmail(
            "invoice@wearecubo.com",
            `Nexus Job Completed-${project.qnq}-${obj.JobNo}-${customer.shortName} ${customer.name}`,
            `-\n
                    QNQ: ${project.qnq}\n
                    Customer: ${customer.name}\n
                    Customer No: ${customer.shortName}\n
                    Project ID: ${obj.Project}\n
                    Job ID: ${obj.JobNo}\n
                    Vehicle Reg: ${vehicle}\n
                    Project Manager: ${project.manager}\n
                    Supplier: ${supplier}\n
                    Engineer: ${engineer}\n
                    BDM: ${customer.BDM}\n
                    Last Actioned: ${this.userData.Name}`
          );
          console.log("email sent");
        })
        .catch(() => {
          this.approvingJob = false;
        });
    },
    editItem(itemId) {
      const itemIndex = this.jobs.findIndex((job) => {
        return job.id === itemId;
      });
      const item = this.jobs[itemIndex];
      this.editedIndex = this.jobs.indexOf(item);
      item.VehicleSite = this.filteredJobs.find(
        (j) => j.id === itemId
      ).VehicleSite;
      this.editedItem = Object.assign({}, this.editedItem, item);
      console.log(this.editedItem);
      if (!item.newMethod) {
        this.selectProducts = this.editedItem.templates;
        let extras = {};
        for (let extraObjKey in item.extras) {
          if (typeof item.extras[extraObjKey] !== "object") {
            extras[extraObjKey] = item.extras ? item.extras[extraObjKey] : "";
          }
          // for (let extraKey in item.extras[extraObjkey]) {
          //   extras[extraKey] = item.extras[extraObjkey][extraKey]
          // }
        }
        let extObj = {};
        for (let p of this.selectProducts) {
          extObj[p] = {};
        }
        extras = { ...extras, ...extObj };
        this.editedItem["extras"] = Object.assign(
          {},
          this.editedItem["extras"],
          extras
        );
      } else {
        this.selectProducts = this.editedItem.products;
      }
      const index = this.projects.findIndex((project) => {
        return (
          project.projectId === this.editedItem.Project &&
          project.Customer === this.activeCustomer
        );
      });
      this.supplierSelect = index > -1 ? this.projects[index].Supplier : null;
      if (this.editedItem.Vehicle) {
        this.vehicleSelect = this.vehicleRegFromID(this.editedItem.Vehicle);
      } else {
        this.vehicleSelect = this.editedItem.vehReg;
      }
      this.dialog = true;
    },
    deleteItem(item) {
      const index = this.jobs.findIndex((job) => {
        return job.id === item.id;
      });
      confirm("Are you sure you want to delete this item?") &&
        fireStore
          .collection("Jobs")
          .doc(item.id)
          .delete()
          .then(() => {
            updateAuditLog(
              "delete",
              "projects",
              this.userData,
              this.jobs[index].JobNo,
              this.jobs[index].Project,
              "deleted job",
              null,
              this.activeCustomer
            );
            this.jobs.splice(index, 1);
            this.setJobStates(this.activeCustomer);
          });
    },
    close() {
      this.dialog = false;
      setTimeout(() => {
        this.productExtras = [];
        this.selectProducts = [];
        this.vehicleSelect = null;
        this.supplierSelect = "";
        this.editedItem = Object.assign({}, this.editedItem, this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },
    save() {
      this.savingSaveJob = true;
      let sortedTemplates = [];
      let deviceArray = [];
      let jobLookupIds = [];
      this.templates.forEach((template) => {
        for (let t of this.editedItem.templateExtras) {
          if (t.product.devices.includes(template.id)) {
            sortedTemplates.push(template.id);
          }
          for (let d of t.templateDetails) {
            if (d.deviceDetails) {
              deviceArray.push({
                product: t.product.id,
                device: d.deviceDetails.id,
              });
              if (d.deviceDetails.IMEI) {
                jobLookupIds.push({
                  IMEI: d.deviceDetails.IMEI,
                  template: d.deviceDetails.type,
                });
              }
              if (d.deviceDetails["DVR ID Number"]) {
                jobLookupIds.push({
                  DVR: d.deviceDetails["DVR ID Number"],
                  template: d.deviceDetails.type,
                });
              }
            }
          }
        }
      });
      if (
        sortedTemplates.indexOf("preCheck") === -1 &&
        sortedTemplates.indexOf("5oJKuVseN0PkmvZeDl0J") === -1
      ) {
        sortedTemplates.unshift("preCheck");
      }
      if (
        sortedTemplates.indexOf("Je2nQqSdazW25XWSGkt3") === -1 &&
        sortedTemplates.indexOf("5oJKuVseN0PkmvZeDl0J") === -1
      ) {
        sortedTemplates.push("Je2nQqSdazW25XWSGkt3");
      }
      jobLookupIds = jobLookupIds.filter((obj, pos, arr) => {
        return (
          arr.map((mapObj) => mapObj["template"]).indexOf(obj["template"]) ===
          pos
        );
      });
      this.editedItem.templates = sortedTemplates.filter(
        (item, pos) => sortedTemplates.indexOf(item) === pos
      );
      this.editedItem.newMethod = true;
      this.editedItem.VehicleSite = this.editedItem.VehicleSite || null;
      let obj = this.editedItem;
      console.log(obj);
      obj.jobLookupIds = jobLookupIds;
      if (obj.extras) {
        for (let extra in obj.extras) {
          if (!obj.extras[extra]) {
            obj.extras[extra] = "";
          }
        }
      }
      delete obj["productExtras"];
      delete obj["templateExtras"];

      if (obj.Project && this.projectsObj[obj.Project]) {
        obj.Supplier = this.projectsObj[obj.Project].Supplier || null;
      }
      if (this.editedIndex > -1) {
        fireStore
          .collection("Jobs")
          .doc(this.editedItem.id)
          .set(obj, { merge: true })
          .then(() => {
            updateAuditLog(
              "update",
              "projects",
              this.userData,
              this.editedItem.JobNo,
              this.editedItem.Project,
              "edited job",
              this.editedItem,
              this.activeCustomer
            );
            Object.assign(this.jobs[this.editedIndex], obj);
            this.savingSaveJob = false;
            for (let device of deviceArray) {
              fireStore.collection("Devices").doc(device.device).set(
                {
                  customer: this.editedItem.Customer,
                  vehicle: this.editedItem.Vehicle,
                  product: device.product,
                },
                { merge: true }
              );
            }
            this.close();
          })
          .catch((error) => {
            this.saveJobFailed = true;
            this.savingSaveJob = false;
            console.log(error);
          });
      } else {
        this.editedItem.Customer = this.activeCustomer;
        obj.Customer = this.activeCustomer;
        const customerIndex = this.customerList.findIndex((customerListObj) => {
          return customerListObj.key === obj.Customer;
        });
        const shortName = this.customerList[customerIndex]["shortName"];
        fireStore
          .collection("Jobs")
          .where("Customer", "==", obj.Customer)
          .get()
          .then((docRef) => {
            let jobNumbers = [];
            docRef.forEach((doc) => {
              let JobNo = doc.data().JobNo;
              if (JobNo && JobNo.indexOf(shortName) > -1) {
                // This check is needed in case the short name has been changed. We only want to increment on job numbers with the new short name.
                JobNo = JobNo.replace(/\D/g, "");
                jobNumbers.push(JobNo);
              }
            });
            jobNumbers = jobNumbers.sort();
            jobNumbers = jobNumbers.reverse();
            if (jobNumbers.length === 0) {
              jobNumbers.push(0);
            }
            this.editedItem.JobNo =
              shortName + this.zeroFill(parseInt(jobNumbers[0]) + 1, 4);
            obj.CreatedDate = Date.now();
            fireStore
              .collection("Jobs")
              .add(obj)
              .then((docRef) => {
                updateAuditLog(
                  "add",
                  "projects",
                  this.userData,
                  this.editedItem.JobNo,
                  this.editedItem.Project,
                  "added job",
                  this.editedItem,
                  this.activeCustomer
                );
                this.editedItem.id = docRef.id;
                obj.id = docRef.id;
                this.jobs.push(obj);
                for (let device of deviceArray) {
                  fireStore.collection("Devices").doc(device.device).set(
                    {
                      customer: this.editedItem.Customer,
                      vehicle: this.editedItem.Vehicle,
                      product: device.product,
                    },
                    { merge: true }
                  );
                }
                this.close();
                this.savingSaveJob = false;
              })
              .catch((error) => {
                console.log(error);
                this.saveJobFailed = true;
                this.savingSaveJob = false;
              });
          })
          .catch((error) => {
            this.savingSaveJob = false;
            this.saveJobFailed = true;
            console.log(error);
          });
      }
    },
    addAdditionalField() {
      if (this.editedItem.additionalFields === undefined) {
        this.editedItem.additionalFields = [];
      }
      this.editedItem.additionalFields.push({ fieldName: "", fieldValue: "" });
    },
    closeProject() {
      this.projectDialog = false;
      this.editedProject = this.defaultProject;
    },
    editProject(item) {
      this.editedProjectIndex = this.projects.indexOf(item);
      if (item.manager && !Array.isArray(item.manager)) {
        item.manager = item.manager.split(", ");
      }
      this.editedProject = Object.assign({}, item);
      this.projectDialog = true;
    },
    deleteProject(deleteJobs) {
      if (deleteJobs === true) {
        this.deletingProjectAll = true;
      } else {
        this.deletingProjectOnly = true;
      }
      const projectId = this.deleteProjectData.projectId;
      const vm = this;
      let index = this.projects.findIndex(function (obj) {
        return obj.id === vm.deleteProjectData.id;
      });
      fireStore
        .collection("Projects")
        .doc(vm.deleteProjectData.id)
        .delete()
        .then(() => {
          updateAuditLog(
            "delete",
            "projects",
            this.userData,
            null,
            this.editedItem.Project,
            "deleted project",
            null,
            this.activeCustomer
          );
          this.projects.splice(index, 1);
        })
        .catch((error) => {
          console.log(error);
        })
        .then(() => {
          this.editedProject = this.defaultProject;
          this.deleteProjectData = {};
          this.deleteProjectDialog = false;
          this.projectDialog = false;
          this.setProjectStates(this.activeCustomer);
          this.deletingProjectOnly = false;
        })
        .then(() => {
          if (deleteJobs === true) {
            var batch = fireStore.batch();
            this.jobs.forEach((job) => {
              if (job.Project === projectId) {
                const batchJob = fireStore.collection("Jobs").doc(job.id);
                batch.delete(batchJob);
                updateAuditLog(
                  "delete",
                  "projects",
                  this.userData,
                  job.JobNo,
                  job.Project,
                  "deleted job",
                  null,
                  this.activeCustomer
                );
              }
            });
            batch.commit().then(() => {
              this.getJobs();
              this.deletingProjectAll = false;
            });
          }
        });
    },
    deleteProjectModal(item) {
      this.deleteProjectData = item;
      this.deleteProjectDialog = true;
    },
    getArchive() {
      this.includeArchive = true;
      this.getProjects();
    },
    saveProject(closeDialog) {
      this.savingSaveProject = true;
      let obj = this.editedProject;
      const id = obj.id;
      obj.Customer = this.activeCustomer || obj.Customer;
      obj.Supplier = obj.Supplier || null;
      obj.manager = obj.manager.join(", ");
      if (this.projectOrdering.indexOf(obj.projectId) !== -1) {
        this.projectOrdering.splice(
          this.projectOrdering.indexOf(obj.projectId),
          1
        );
      }
      if (obj.priorityOrder > 0) {
        this.projectOrdering.splice(obj.priorityOrder - 1, 0, obj.projectId);
      }
      fireStore
        .collection("ProjectOrdering")
        .doc("order")
        .set({ array: this.projectOrdering });
      fireStore
        .collection("Projects")
        .doc(id)
        .update(obj)
        .then(() => {
          const index = this.projects.findIndex((project) => {
            return project.id === obj.id;
          });
          updateAuditLog(
            "update",
            "projects",
            this.userData,
            null,
            this.projects[index].projectId,
            "edited project",
            obj,
            obj.Customer
          );
          let customer = this.customerList.find((a) => a.key === obj.Customer);
          let supplier = this.supplierNameFromID(obj.Supplier);
          if (this.projects[index].Supplier !== obj.Supplier) {
            sendEmail(
              "invoice@wearecubo.com",
              `*Project* - (Cubo) - Supplier assigned to -${customer.shortName} ${customer.name}`,
              `-\n
                    Customer: ${customer.name}\n
                    Customer No: ${customer.shortName}\n
                    Project ID: ${obj.projectId}\n
                    BDM: ${customer.BDM}\n
                    QNQ: ${obj.qnq}\n
                    Customer PO: ${obj.customerPO}\n
                    Project Manager: ${obj.manager}\n
                    Project Status: ${obj.Status}\n
                    Supplier: ${supplier}\n
                    https://nexus.mycubo.online/#/Project/${customer.key}/p:${obj.projectId}
                   `
            );
          }
          if (obj.Status == "Complete") {
            sendEmail(
              "invoice@wearecubo.com",
              `Nexus Project Completed-${obj.qnq}-${obj.projectId}-${customer.shortName} ${customer.name}`,
              `-\n
                    QNQ: ${obj.qnq}\n
                    Customer: ${customer.name}\n
                    Customer No: ${customer.shortName}\n
                    Project ID: ${obj.projectId}\n
                    Project Manager: ${obj.manager}\n
                    Supplier: ${supplier}\n
                    BDM: ${customer.BDM}\n
                    Last Actioned: ${this.userData.Name}`
            );
          }
          this.projects.splice(index, 1, obj);
          this.setProjectStates(this.activeCustomer);
          this.savingSaveProject = false;
          if (closeDialog === true) {
            this.projectDialog = false;
          }
        })
        .catch((error) => {
          console.log(error);
          this.saveProjectFailed = true;
          this.savingSaveProject = false;
        });
    },
    addJobsToProject() {
      this.savingAddJobsToProject = true;
      const project = this.assignToProject;
      const supplier =
        this.projectsObj[project].Supplier !== undefined
          ? this.projectsObj[project].Supplier
          : null;
      let batch = fireStore.batch();
      let vmJobs = this.jobs;
      this.selected.forEach((job, index) => {
        const ref = fireStore.collection("Jobs").doc(job.id);
        batch.update(ref, { Project: project, Supplier: supplier });
        updateAuditLog(
          "update",
          "projects",
          this.userData,
          job.JobNo,
          project,
          "changed jobs project",
          null,
          this.activeCustomer
        );
      });
      batch
        .commit()
        .then(() => {
          this.selected.forEach((job, index) => {
            const vmIndex = vmJobs.findIndex((vmJob) => {
              return vmJob.id === job.id;
            });
            vmJobs[vmIndex]["Project"] = project;
            vmJobs[vmIndex]["Supplier"] = supplier;
          });
          this.jobs = vmJobs;
        })
        .catch((error) => {
          console.log(error);
        })
        .then(() => {
          this.assignToProject = null;
          this.savingAddJobsToProject = false;
          this.addToProjectDialog = false;
        });
    },
    assignJobsToEngineer() {
      this.savingAssignJobsToEngineer = true;
      const supplier = this.assignToSupplier;
      const engineer = this.assignToEngineer;
      let batch = fireStore.batch();
      let vmJobs = this.jobs;
      this.selected.forEach((job, index) => {
        const ref = fireStore.collection("Jobs").doc(job.id);
        batch.update(ref, { Allocated: engineer, Supplier: supplier });
        updateAuditLog(
          "update",
          "projects",
          this.userData,
          job.JobNo,
          job.Project,
          "changed jobs engineer",
          { Allocated: engineer, Supplier: supplier },
          this.activeCustomer
        );
      });
      batch
        .commit()
        .then(() => {
          this.selected.forEach((job, index) => {
            const vmIndex = vmJobs.findIndex((vmJob) => {
              return vmJob.id === job.id;
            });
            vmJobs[vmIndex]["Supplier"] = supplier;
            vmJobs[vmIndex]["Allocated"] = engineer;
          });
          this.jobs = vmJobs;
        })
        .catch((error) => {
          console.log(error);
        })
        .then(() => {
          this.savingAssignJobsToEngineer = false;
          this.assignToSupplier = "";
          this.assignToEngineer = "";
          this.assignToEngineerDialog = false;
        });
    },
    assignInstallDates() {
      this.assigningDates = true;
      const date = this.assignInstallDate;
      let batch = fireStore.batch();
      let vmJobs = this.jobs;
      this.selected.forEach((job, index) => {
        const ref = fireStore.collection("Jobs").doc(job.id);
        batch.update(ref, { Date: date });
        updateAuditLog(
          "update",
          "projects",
          this.userData,
          job.JobNo,
          job.Project,
          "changed install date",
          { Date: date },
          this.activeCustomer
        );
      });
      batch
        .commit()
        .then(() => {
          this.selected.forEach((job, index) => {
            const vmIndex = vmJobs.findIndex((vmJob) => {
              return vmJob.id === job.id;
            });
            vmJobs[vmIndex]["Date"] = date;
          });
          this.jobs = vmJobs;
        })
        .catch((error) => {
          console.log(error);
        })
        .then(() => {
          this.assigningDates = false;
          this.assignInstallDate = "";
          this.assignInstallDateDialog = false;
        });
    },
    deleteMultiJobs() {
      this.deletingJobs = true;
      let batch = fireStore.batch();
      let vmJobs = this.jobs;
      this.selected.forEach((job, index) => {
        const ref = fireStore.collection("Jobs").doc(job.id);
        batch.delete(ref);
        updateAuditLog(
          "delete",
          "projects",
          this.userData,
          job.JobNo,
          job.Project,
          "deleted multiple jobs",
          null,
          this.activeCustomer
        );
      });
      batch
        .commit()
        .then(() => {
          this.selected.forEach((job, index) => {
            const vmIndex = vmJobs.findIndex((vmJob) => {
              return vmJob.id === job.id;
            });
            vmJobs.splice(vmIndex, 1);
          });
          this.jobs = vmJobs;
        })
        .catch((error) => {
          console.log(error);
        })
        .then(() => {
          this.setJobStates(this.activeCustomer);
          this.deletingJobs = false;
          this.deleteJobsDialog = false;
          this.selected = [];
        });
    },
    createDuplicateProject() {
      let projectTemplate = {};
      for (let project in this.projects) {
        if (this.projects[project].projectId === this.duplicateProjectSource) {
          projectTemplate = JSON.parse(JSON.stringify(this.projects[project]));
          break;
        }
      }
      projectTemplate.Supplier = this.duplicateProjectSupplier;
      let hasID = false;
      let i = 0;
      while (hasID === false) {
        i++;
        if (
          this.projectsObj[projectTemplate.projectId + "-" + i] === undefined
        ) {
          projectTemplate.projectId = projectTemplate.projectId + "-" + i;
          hasID = true;
        }
      }
      fireStore
        .collection("Projects")
        .add(projectTemplate)
        .then((docRef) => {
          updateAuditLog(
            "add",
            "projects",
            this.userData,
            null,
            projectTemplate.projectId,
            "duplicated project",
            projectTemplate,
            this.activeCustomer
          );
          this.assignToProject = projectTemplate.projectId;
          this.createNewProject = false;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    approveMultiJobs() {
      this.approvingJobs = true;
      let batch = fireStore.batch();
      let vmJobs = this.jobs;
      this.selected.forEach((job, index) => {
        const vmIndex = vmJobs.findIndex((vmJob) => {
          return vmJob.id === job.id;
        });
        let completedDate =
          vmJobs[vmIndex].CompletedDate || moment().format("YYYY-MM-DD");
        let installedDevices = vmJobs[vmIndex].finance || {};
        for (let device in installedDevices) {
          if (typeof installedDevices[device] !== "string" && typeof installedDevices[device] !== "number") {
            installedDevices[device].installDate = completedDate;
            installedDevices[device].project = vmJobs[vmIndex].Project;
            installedDevices[device].job = vmJobs[index].id;
          }
        }
        // vmJobs[vmIndex]['templates'].forEach(template => {
        //   if (template !== 'preCheck') {
        //     installedDevices[template] = true
        //   }
        // })
        let obj = {
          devices: vmJobs[vmIndex]["extras"],
          installedDevices: installedDevices,
        };

        const vehIndex = this.vehicles.findIndex((vehicle) => {
          return vehicle.VehicleReg === job.vehReg;
        });
        let vehDoc;
        if (vehIndex > -1) {
          vehDoc = this.vehicles[vehIndex]["key"];
          const vehRef = fireStore.collection("Vehicles").doc(vehDoc);
          batch.update(vehRef, obj);
        }
        let deviceArray = [];
        for (let template of Object.keys(installedDevices)) {
          if (
            this.products.find((p) => {
              return template === p.id;
            }) === undefined
          ) {
            continue;
          }
          let deviceKey = this.products.find((p) => {
            return template === p.id;
          }).devices[0];
          let device = this.templates.find((t) => {
            return deviceKey === t.id;
          });
          if (device) {
            device.deviceDetails = this.devices.find((d) => {
              return d.type === device.id && d.vehicle === vehDoc;
            });
            if (device.deviceDetails) {
              deviceArray.push(device.deviceDetails.id);
              const deviceRef = fireStore
                .collection("Devices")
                .doc(device.deviceDetails.id);
              batch.update(deviceRef, {
                InstallDate: completedDate,
                stockLocation: "Installed",
              });
            }
          }
        }
        const jobRef = fireStore.collection("Jobs").doc(job.id);
        batch.update(jobRef, {
          Status: 3,
          CompletedDate: completedDate,
          devicesInstalled: deviceArray,
        });
        console.log("send email");
        let project =
          this.projects.find((a) => a.projectId === job.Project) || {};
        let customer = this.customerList.find(
          (a) => a.key === this.activeCustomer
        );
        let engineer = this.engineerNameFromID(job.Allocated);
        let supplier = this.supplierNameFromID(project.Supplier);
        let vehicle = job.reg || this.vehicleRegFromID(job.Vehicle);
        sendEmail(
          "invoice@wearecubo.com",
          `Nexus Job Completed-${project.qnq}-${job.JobNo}-${customer.shortName} ${customer.name}`,
          `-\n
                    QNQ: ${project.qnq}\n
                    Customer: ${customer.name}\n
                    Customer No: ${customer.shortName}\n
                    Project ID: ${job.Project}\n
                    Job ID: ${job.JobNo}\n
                    Vehicle Reg: ${vehicle}\n
                    Project Manager: ${project.manager}\n
                    Supplier: ${supplier}\n
                    Engineer: ${engineer}\n
                    BDM: ${customer.BDM}\n
                    Last Actioned: ${this.userData.Name}`
        );
        console.log("email sent");
        updateAuditLog(
          "update",
          "projects",
          this.userData,
          job.JobNo,
          job.Project,
          "approved multiple jobs",
          null,
          this.activeCustomer
        );
      });
      batch
        .commit()
        .then(() => {
          this.selected.forEach((job) => {
            const vmIndex = vmJobs.findIndex((vmJob) => {
              return vmJob.id === job.id;
            });
            vmJobs[vmIndex]["Status"] = 3;
            if (!vmJobs[vmIndex]["CompletedDate"]) {
              vmJobs[vmIndex]["CompletedDate"] = moment().format("YYYY-MM-DD");
            }
          });
          this.jobs = vmJobs;
        })
        .catch((error) => {
          console.log(error);
        })
        .then(() => {
          this.setJobStates(this.activeCustomer);
          this.approvingJobs = false;
          this.approveJobsDialog = false;
          this.selected = [];
        });
    },
    setJobSupplier() {
      if (this.editedItem.Project) {
        const index = this.projects.findIndex((project) => {
          return project.projectId === this.editedItem.Project;
        });
        if (index > -1 && this.projects[index]["Supplier"]) {
          this.supplierSelect = this.projects[index]["Supplier"];
        } else {
          this.supplierSelect = null;
          this.editedItem.Allocated = "";
        }
      } else {
        this.supplierSelect = null;
        this.editedItem.Allocated = "";
      }
    },
    zeroFill(number, width) {
      width -= number.toString().length;
      if (width > 0) {
        return (
          new Array(width + (/\./.test(number) ? 2 : 1)).join("0") + number
        );
      }
      return number;
    },
    vehicleRegFromID(id) {
      const index = this.vehicles.findIndex((vehicle) => {
        return vehicle.key === id;
      });
      if (index > -1) {
        if (
          this.vehicles[index]["VehicleReg"] &&
          this.vehicles[index]["VehicleReg"].length > 0
        ) {
          return this.vehicles[index]["VehicleReg"];
        } else {
          return this.vehicles[index]["friendlyId"];
        }
      } else {
        return "";
      }
    },
    engineerNameFromID(id) {
      const index = this.engineers.findIndex((engineer) => {
        return engineer.key === id;
      });
      if (index > -1) {
        return this.engineers[index]["name"];
      } else {
        return "";
      }
    },
    supplierNameFromID(id) {
      const index = this.suppliers.findIndex((supplier) => {
        return supplier.key === id;
      });
      return index > -1 ? this.suppliers[index].name : null;
    },
    customerNameFromID(id) {
      const index = this.customerList.findIndex((customer) => {
        return customer.key === id;
      });
      return index > -1 ? this.customerList[index].name : null;
    },
    updatedVehicleReg(regObj) {
      const vehIndex = this.vehicles.findIndex((vehicle) => {
        return vehicle.id === regObj.id;
      });
      if (vehIndex > -1) {
        this.vehicles[vehIndex]["VehicleReg"] = regObj.newReg;
      }
    },
    mergePropsNotes(firstNote, secondNote) {
      let completeNote = "";
      if (firstNote) {
        completeNote += firstNote;
      }
      if (secondNote) {
        if (completeNote.length < 1) {
          completeNote += secondNote;
        } else {
          completeNote += "&" + secondNote;
        }
      }
      return completeNote;
    },
  },
};
</script>



// WEBPACK FOOTER //
// Project.vue?6006e84a